import { useState, useEffect, useRef, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import type { 
  Flow, 
  Card,
  Departamento, 
  Colaborador, 
  Automacao,
  User,
  UserRole,
  Empresa,
  LoginCredentials,
  UserSession,
  Task
} from './types';
import { filterByUserCompany } from './utils/companyFilter';
import { getApiBase } from './utils/apiBase';

// Contextos
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { SocketProvider } from './contexts/SocketContext';
import { DataLoadingProvider, useDataLoading } from './contexts/DataLoadingContext';

// Componentes
import Landing from './components/Landing';
import Terms from './components/Terms';
import Login from './components/Login';
import Cadastro from './components/Cadastro';
import Verificacao from './components/Verificacao';
import AuthRedirect from './components/AuthRedirect';
import FlowList from './components/FlowList';
import KanbanBoard from './components/KanbanBoard';
import RamboArcade from './components/KanbanBoard/RamboArcade';
import Departamentos from './components/Departamentos';
import Colaboradores from './components/Colaboradores';
import Organograma from './components/Organograma';
import ProcedimentosSimples from './components/ProcedimentosSimples';
import ColaboradorModal from './components/Colaboradores/ColaboradorModal';
import Automacoes from './components/Automacoes';
import Empresas from './components/Empresas';
import ErrorBoundary from './components/ErrorBoundary';
import ResetPassword from './components/Login/ResetPassword';
import Dashboard from './components/Dashboard';
import Emails from './components/Emails';
import HomeComponent from './components/Home';
import TasksComponent from './components/Tasks';
import IntegracoesPage from './components/Integracoes/IntegracoesPage';
import CRMComunicacao from './components/CRM/Comunicacao';
import CRMPipeline from './components/CRM/Pipeline';
import CRMMinhaCarteira from './components/CRM/MinhaCarteira';
import CRMCarteiraGix from './components/CRM/CarteiraGix';
import CRMContatos from './components/CRM/Contatos';
import CRMEmpresasConta from './components/CRM/EmpresasConta';
import CRMLeads from './components/CRM/Leads';
import CRMConfiguracoes from './components/CRM/Configuracoes';
import DataLoadingIndicator from './components/DataLoadingIndicator';
import EscaloneLoading from './components/EscaloneLoading';
import Whatsapp from './components/Whatsapp';
import Chat from './components/Chat/ChatContainer';
import ChatNotifier from './components/Chat/ChatNotifier';
import CardRedirect from './components/CardRedirect';
import TaskRedirect from './components/TaskRedirect';
import PortalPublico from './components/Portais/PortalPublico';
import DiscPublico from './components/Atlas/DISC/DiscPublico';
import CardsConectados from './components/CardsConectados';
import Cube from './components/Cube';
import UpgradeScreen from './components/UpgradeScreen';
import TrialBanner from './components/TrialBanner';
import OnboardingTour from './components/OnboardingTour';
import Planos from './components/Planos';
import type { PlanoSelecionado } from './components/Planos';
import CheckoutModal from './components/CheckoutModal';
import ProductSelector from './components/ProductSelector';
import FinanceApp from './components/Finance';
import AtlasApp from './components/Atlas';
import AvisoNovaVersao from './components/AvisoNovaVersao';
import { ehAdminMaster } from './utils/adminMaster';

// ================================================================================
// 🚀 CACHE LOCAL COM INDEXEDDB - Suporta dados grandes (50MB+)
// ================================================================================
const FLOW_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 horas de validade máxima
const DB_NAME = 'escalone_cache';
const DB_VERSION = 1;
const STORE_NAME = 'flows';

interface CachedFlow {
  flowId: string;
  flow: Flow;
  timestamp: number;
}

// Abre/cria o banco IndexedDB
const openCacheDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'flowId' });
        console.log('🗄️ IndexedDB store criado');
      }
    };
  });
};

// Salva flow no cache IndexedDB (async, sem bloquear)
const saveFlowToCache = async (flowId: string, flow: Flow): Promise<void> => {
  try {
    const db = await openCacheDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const cacheData: CachedFlow = {
      flowId,
      flow,
      timestamp: Date.now()
    };
    
    store.put(cacheData);
    
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        db.close();
        console.log('💾 Flow salvo no IndexedDB:', flow.name, `(${flow.cards?.length || 0} cards)`);
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  } catch (error) {
    console.warn('⚠️ Erro ao salvar cache IndexedDB:', error);
  }
};

// Recupera flow do cache IndexedDB
const getFlowFromCache = async (flowId: string): Promise<Flow | null> => {
  try {
    const db = await openCacheDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(flowId);
    
    return new Promise((resolve) => {
      request.onsuccess = () => {
        db.close();
        const cacheData = request.result as CachedFlow | undefined;
        
        if (!cacheData) {
          resolve(null);
          return;
        }
        
        // Verificar se o cache expirou (24h)
        if (Date.now() - cacheData.timestamp > FLOW_CACHE_TTL) {
          console.log('🗑️ Cache expirado, será atualizado em background');
          // Retorna mesmo assim para Stale-While-Revalidate
          resolve(cacheData.flow);
          return;
        }
        
        const ageSeconds = Math.round((Date.now() - cacheData.timestamp) / 1000);
        console.log(`⚡ Flow carregado do CACHE instantaneamente! (idade: ${ageSeconds}s)`, cacheData.flow.name);
        resolve(cacheData.flow);
      };
      request.onerror = () => {
        db.close();
        console.warn('⚠️ Erro ao ler cache IndexedDB');
        resolve(null);
      };
    });
  } catch (error) {
    console.warn('⚠️ IndexedDB não disponível:', error);
    return null;
  }
};

// Limpa o cache de um flow específico (força recarregar do servidor)
const clearFlowCache = async (flowId: string): Promise<void> => {
  try {
    const db = await openCacheDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(flowId);
    
    return new Promise((resolve) => {
      tx.oncomplete = () => {
        db.close();
        console.log('🗑️ Cache do flow limpo:', flowId);
        resolve();
      };
      tx.onerror = () => {
        db.close();
        resolve();
      };
    });
  } catch (error) {
    console.warn('⚠️ Erro ao limpar cache:', error);
  }
};

// Atualiza o cache com os cards atuais (em vez de invalidar/deletar)
// Isso garante que o F5 sempre carregue rápido com dados atualizados
// 🔄 MERGE: Preserva etapaHistory e fieldHistory do cache original
const updateFlowCacheWithCards = async (flowId: string, cards: Card[]): Promise<void> => {
  try {
    const db = await openCacheDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    // Buscar o flow atual do cache
    const getRequest = store.get(flowId);
    
    return new Promise((resolve) => {
      getRequest.onsuccess = () => {
        const cacheData = getRequest.result as CachedFlow | undefined;
        
        if (cacheData && cacheData.flow) {
          // 🔄 Criar mapa dos cards do cache para preservar dados pesados
          const cachedCardsMap = new Map<string, Card>();
          (cacheData.flow.cards || []).forEach((c: Card) => {
            if (c.id) cachedCardsMap.set(c.id, c);
          });
          
          // 🔄 Mesclar cards: usar dados novos mas preservar etapaHistory/fieldHistory do cache
          const mergedCards = cards.map(newCard => {
            const cachedCard = cachedCardsMap.get(newCard.id);
            if (cachedCard) {
              return {
                ...newCard,
                // Preservar campos pesados do cache se não existirem no novo card
                etapaHistory: newCard.etapaHistory || cachedCard.etapaHistory,
                fieldHistory: newCard.fieldHistory || cachedCard.fieldHistory
              };
            }
            return newCard;
          });
          
          const updatedFlow = {
            ...cacheData.flow,
            cards: mergedCards
          };
          
          const newCacheData: CachedFlow = {
            flowId,
            flow: updatedFlow,
            timestamp: Date.now()
          };
          
          store.put(newCacheData);
          console.log('🔄 Cache atualizado com', mergedCards.length, 'cards (históricos preservados)');
        }
        
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          resolve();
        };
      };
      
      getRequest.onerror = () => {
        db.close();
        resolve();
      };
    });
  } catch (error) {
    console.warn('⚠️ Erro ao atualizar cache:', error);
  }
};

// Limpa caches antigos (mais de 7 dias sem uso)
const cleanupOldCaches = async (): Promise<void> => {
  try {
    const db = await openCacheDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    
    request.onsuccess = () => {
      const allCaches = request.result as CachedFlow[];
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 dias
      let removed = 0;
      
      allCaches.forEach(cache => {
        if (Date.now() - cache.timestamp > maxAge) {
          store.delete(cache.flowId);
          removed++;
        }
      });
      
      if (removed > 0) {
        console.log('🧹 Caches antigos limpos:', removed);
      }
    };
    
    tx.oncomplete = () => db.close();
    tx.onerror = () => db.close();
  } catch (error) {
    console.warn('⚠️ Erro ao limpar caches:', error);
  }
};
// ================================================================================

// Ícones
import { 
  Home,
  LayoutGrid,
  BarChart2,
  Building,
  Building2,
  Users as UsersIcon,
  Network,
  FileText,
  Settings,
  Menu as LucideMenu,
  X,
  ChevronDown as LucideChevronDown,
  MessageSquare,
  LogOut,
  ArrowLeftRight,
  Wrench,
  Mail,
  MessageCircle,
  Cloud,
  CheckSquare,
  Link2 as LinkIcon,
  Box,
  CreditCard,
  Briefcase,
  Target,
  Contact as ContactIcon,
  UserPlus,
  Tag
} from 'lucide-react';

interface MenuItem {
  id: string;
  name: string;
  icon: any;
  subItems?: {
    id: string;
    name: string;
    icon: any;
    description?: string;
  }[];
}

const App = () => {
  // Loading system
  const { showLoading, hideLoading, updateMessage, loading } = useLoading();
  const { setLoadingState } = useDataLoading();
  // Modo estrito: não operar sem backend/DB
  const [fatalError, setFatalError] = useState<string | null>(null);
  // Toast
  const toast = useToast();
  
  // Hook para navegação
  const navigate = useNavigate();
  const location = useLocation();

  // Auth state
  const [session, setSession] = useState<UserSession | null>(null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [verificacaoEmail, setVerificacaoEmail] = useState<{email: string, codigo: string, senha?: string} | null>(null);
  const [tempUser, setTempUser] = useState<User | null>(null);
  const [step, setStep] = useState<'credentials' | 'empresa'>('credentials');
  
  // 🏦 Seleção de produto (Escalone vs Finance)
  const [selectedProduct, setSelectedProduct] = useState<'escalone' | 'finance' | 'atlas' | null>(() => {
    const stored = localStorage.getItem('selectedProduct');
    return stored as 'escalone' | 'finance' | 'atlas' | null;
  });

  // Navigation state
  const [menuOpen, setMenuOpen] = useState(true);
  // � Easter egg: arcade jogável — clique no logo "E" abre o joguinho
  const [activeIntro, setActiveIntro] = useState<0 | 1>(0);
  const playNextIntro = () => {
    if (activeIntro !== 0) return; // já tocando
    setActiveIntro(1);
  };
  const [currentSection, setCurrentSection] = useState(() => {
    // Detectar parâmetros na URL durante inicialização
    const urlParams = new URLSearchParams(location.search);
    const departamentoId = urlParams.get('d');
    if (departamentoId) {
      console.log('🔗 Departamento detectado na URL (inicialização):', departamentoId);
      return 'departamentos';
    }
    return 'home';
  });
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['organizacoes']);
  const [chatOpenKey, setChatOpenKey] = useState(0); // Usar key para forçar re-render
  const [chatCanalInicial, setChatCanalInicial] = useState<string | undefined>(undefined); // Conversa vinda da notificação
  const [chatCanalNome, setChatCanalNome] = useState<string | undefined>(undefined); // Nome do remetente da notificação
  const [chatNaoLidas, setChatNaoLidas] = useState(0); // Total de não lidas (badge do menu)
  const [crmInboxNumero, setCrmInboxNumero] = useState<string | undefined>(undefined);

  // Detect mobile viewport (md breakpoint ~768px)
  const isMobileViewport = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  };

  const collapseMenuIfDesktop = () => {
    if (!isMobileViewport()) setMenuOpen(false);
  };
  const restoreMenuIfDesktop = () => {
    if (!isMobileViewport()) setMenuOpen(true);
  };

  // Listener para eventos de esconder/mostrar menu via componentes filhos
  useEffect(() => {
    const handleHideMenu = () => setMenuOpen(false);
    const handleShowMenu = () => { if (!isMobileViewport()) setMenuOpen(true); };
    window.addEventListener('escalone:hideMenu', handleHideMenu);
    window.addEventListener('escalone:showMenu', handleShowMenu);
    return () => {
      window.removeEventListener('escalone:hideMenu', handleHideMenu);
      window.removeEventListener('escalone:showMenu', handleShowMenu);
    };
  }, []);

  // 🔔 Notificação do FlowChat clicada em qualquer tela → abre o chat na conversa
  useEffect(() => {
    const abrirChat = (e: Event) => {
      const detalhe = (e as CustomEvent).detail || {};
      setChatCanalInicial(detalhe.channelId || undefined);
      setChatCanalNome(detalhe.nome || undefined);
      setChatOpenKey(prev => prev + 1);
    };
    const atualizarNaoLidas = (e: Event) => {
      setChatNaoLidas((e as CustomEvent).detail?.total || 0);
    };
    window.addEventListener('flowchat:abrir', abrirChat);
    window.addEventListener('flowchat:naolidas', atualizarNaoLidas);
    return () => {
      window.removeEventListener('flowchat:abrir', abrirChat);
      window.removeEventListener('flowchat:naolidas', atualizarNaoLidas);
    };
  }, []);

  // Navigate to a section and auto-close menu on mobile
  const navigateToSection = (sectionId: string) => {
    // Se clicar em Chat, abrir o FlowChat
    if (sectionId === 'chat') {
      setChatCanalInicial(undefined); // Abertura manual: sem conversa forçada
      setChatCanalNome(undefined);
      setChatOpenKey(prev => prev + 1); // Incrementar para forçar abertura
      return; // Não mudar a seção, apenas abrir o chat
    }
    
    // Se clicar em Fluxos e houver um flow aberto, voltar para lista de flows
    if (sectionId === 'flows' && selectedFlow) {
      setSelectedFlow(null);
    }
    
    setCurrentSection(sectionId);
    if (isMobileViewport() || sectionId === 'cube') {
      setMenuOpen(false);
    }
  };

  // Listener para abrir Inbox de WhatsApp em uma conversa específica (vindo do Pipeline ou CarteiraGix)
  useEffect(() => {
    const handler = (e: Event) => {
      const numero = (e as CustomEvent).detail?.numero;
      if (numero) setCrmInboxNumero(numero);
      setCurrentSection('crm-comunicacao');
    };
    window.addEventListener('crm-open-inbox', handler);
    return () => window.removeEventListener('crm-open-inbox', handler);
  }, []);
  
  // Flows state
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [flows, setFlows] = useState<Flow[]>([]);
  // Cards em formato enxuto para Home/Dashboards — a lista de fluxos vem sem os
  // cards (excludeCards=true), então essas telas buscam o resumo à parte.
  const [cardsResumo, setCardsResumo] = useState<Card[]>([]);
  const [flowGlobalConfig, setFlowGlobalConfig] = useState<any[]>([]); // configs por colaborador
  // Permissões granulares por fluxo (carregadas no Modal de Configuração de Permissões)
  const [flowPermissoes, setFlowPermissoes] = useState<any[]>([]);
  const [planoInfo, setPlanoInfo] = useState<any>(null); // info do plano/trial
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const [showPlanosScreen, setShowPlanosScreen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkoutPlano, setCheckoutPlano] = useState<PlanoSelecionado | null>(null);
  const [cardToOpen, setCardToOpen] = useState<string | null>(null);
  const [pendingShortCodeCard, setPendingShortCodeCard] = useState<{ flowId: string; cardId: string } | null>(null);
  const [loadingShortCode, setLoadingShortCode] = useState<boolean>(() => {
    // Detectar se URL é /c/:shortCode na inicialização
    const path = window.location.pathname;
    return /^\/c\/[A-Fa-f0-9]{6,7}$/.test(path);
  });
  
  // 🔗 Estados para detectar URL /flows/:flowId e abrir fluxo automaticamente no F5
  const [pendingFlowUrl, setPendingFlowUrl] = useState<{ flowId: string; cardShortCode?: string } | null>(() => {
    // Detectar se URL é /flows/:flowId ou /flows/:flowId/card/:shortCode na inicialização
    const path = window.location.pathname;
    const flowMatch = path.match(/^\/flows\/([a-f0-9]{24})(?:\/card\/([A-Fa-f0-9]{6,7}))?$/);
    if (flowMatch && flowMatch[1]) {
      return { flowId: flowMatch[1], cardShortCode: flowMatch[2] || undefined };
    }
    return null;
  });
  const [loadingFlowUrl, setLoadingFlowUrl] = useState<boolean>(() => {
    // Detectar se URL é /flows/:flowId na inicialização
    const path = window.location.pathname;
    return /^\/flows\/[a-f0-9]{24}/.test(path);
  });
  
  // 🔗 Estados para detectar URL /procedimento/:departamentoId/:procedimentoId
  const [pendingProcedimentoUrl, setPendingProcedimentoUrl] = useState<{ departamentoId: string; procedimentoId: string } | null>(() => {
    const path = window.location.pathname;
    const procMatch = path.match(/^\/procedimento\/([a-f0-9]{24})\/([a-f0-9]+)$/);
    if (procMatch && procMatch[1] && procMatch[2]) {
      return { departamentoId: procMatch[1], procedimentoId: procMatch[2] };
    }
    return null;
  });
  const [loadingProcedimentoUrl, setLoadingProcedimentoUrl] = useState<boolean>(() => {
    const path = window.location.pathname;
    return /^\/procedimento\/[a-f0-9]{24}\/[a-f0-9]+$/.test(path);
  });
  
  // Organization state
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [selectedDepartamentoId, setSelectedDepartamentoId] = useState<string | null>(null);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [automacoes, setAutomacoes] = useState<Automacao[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  // const [convites, setConvites] = useState<Convite[]>([]);
  
  // Tasks state
  const [tasks, setTasks] = useState<Task[]>([]);

  // Modal state
  const [viewingColaborador, setViewingColaborador] = useState<Colaborador | null>(null);
  const [editingColaborador, setEditingColaborador] = useState<Colaborador | null>(null);
  
  // User menu state
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Logout state removido; exigimos login sempre
  
  // Menu items
  const menuItems: MenuItem[] = [
    { id: 'home', name: 'Home', icon: Home },
    { id: 'flows', name: 'Fluxos', icon: LayoutGrid,
      subItems: [
        {
          id: 'cards-conectados',
          name: 'Cards Conectados',
          icon: LinkIcon,
          description: 'Configure subprocessos e cards conectados entre fluxos'
        },
        {
          id: 'cube',
          name: 'Cube',
          icon: Box,
          description: 'Construtor de relatórios dinâmicos com dados dos fluxos'
        }
      ]
    },
    { id: 'tasks', name: 'Tarefas', icon: CheckSquare },
    { id: 'chat', name: 'Chat', icon: MessageSquare },
    { id: 'dashboards', name: 'Dashboards', icon: BarChart2 },
    { 
      id: 'organizacoes', 
      name: 'Organizações', 
      icon: Building,
      subItems: [
        { 
          id: 'empresas',
          name: 'Empresas',
          icon: Building,
          description: 'Gerencie as empresas do sistema'
        },
        { 
          id: 'departamentos', 
          name: 'Departamentos', 
          icon: Building2,
          description: 'Gerencie os departamentos da empresa'
        },
        { 
          id: 'colaboradores', 
          name: 'Colaboradores', 
          icon: UsersIcon,
          description: 'Gerencie os colaboradores e suas atribuições'
        },
        { 
          id: 'organograma', 
          name: 'Organograma', 
          icon: Network,
          description: 'Visualize e gerencie a estrutura organizacional'
        }
      ]
    },
    { 
      id: 'configuracoes', 
      name: 'Configurações', 
      icon: Settings,
      subItems: [
        {
          id: 'automacoes',
          name: 'Automações',
          icon: Wrench,
          description: 'Configure automações para seus fluxos de trabalho'
        },
        {
          id: 'emails',
          name: 'E-mails',
          icon: Mail,
          description: 'Configure templates e automações de e-mail'
        },
        {
          id: 'whatsapp',
          name: 'WhatsApp',
          icon: MessageCircle,
          description: 'Configure templates e automações de WhatsApp Business'
        },
        {
          id: 'integracoes',
          name: 'Integrações',
          icon: Cloud,
          description: 'Configure integrações com Google Drive'
        },
        {
          id: 'planos',
          name: 'Planos',
          icon: CreditCard,
          description: 'Gerencie seu plano e assinatura'
        }
      ]
    },
  ];

  // Função auxiliar para filtrar dados por empresa (não utilizada neste arquivo)

  // Função para remover duplicatas baseado no ID
  const removeDuplicates = (data: any[]) => {
    const seen = new Set();
    return data.filter(item => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  };

  // 🚀 FUNÇÕES DE CARREGAMENTO PROGRESSIVO - Cada uma carrega um tipo de dado independentemente
  
  // ⚡ CARREGAMENTO RÁPIDO: Busca um flow específico pelo ID (COM CARDS)
  // Usa estratégia "Stale-While-Revalidate" para carregamento instantâneo
  const loadSingleFlow = async (flowId: string, useCache: boolean = true): Promise<Flow | null> => {
    try {
      const startTime = performance.now();
      console.log('⚡ Carregando flow específico COM cards:', flowId);
      
      // 🚀 CACHE: Tentar carregar do IndexedDB primeiro (instantâneo)
      if (useCache) {
        const cachedFlow = await getFlowFromCache(flowId);
        if (cachedFlow) {
          console.log(`⚡ [${Math.round(performance.now() - startTime)}ms] Flow carregado do CACHE instantaneamente!`);
          
          // Retornar cache e atualizar em background
          setTimeout(async () => {
            console.log('🔄 Atualizando flow em background...');
            const freshFlow = await loadSingleFlow(flowId, false); // Sem cache para buscar do servidor
            if (freshFlow) {
              await saveFlowToCache(flowId, freshFlow);
              // Atualizar o state se o usuário ainda estiver neste flow
              setSelectedFlow(prev => {
                if (prev && (prev.id === flowId || prev._id === flowId)) {
                  console.log('🔄 Flow atualizado em background:', freshFlow.name);
                  return freshFlow;
                }
                return prev;
              });
            }
          }, 100);
          
          return cachedFlow;
        }
      }
      
      // Carregar o flow COM os cards (necessário para o Kanban funcionar)
      const response = await fetch(`/api/flows/${flowId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      console.log(`📡 [${Math.round(performance.now() - startTime)}ms] Resposta recebida`);

      if (!response.ok) {
        console.warn('⚠️ Flow não encontrado:', flowId);
        return null;
      }

      const result = await response.json();
      console.log(`📦 [${Math.round(performance.now() - startTime)}ms] JSON parseado`);
      
      if (result.success && result.data) {
        const flow = result.data;
        // Normalizar o flow
        const normalizedFlow = {
          ...flow,
          id: flow.id || flow._id,
          cards: flow.cards || []
        };
        console.log(`✅ [${Math.round(performance.now() - startTime)}ms] Flow carregado:`, normalizedFlow.name, `(${normalizedFlow.cards?.length || 0} cards)`);
        
        // 💾 Salvar no IndexedDB para próxima vez (não bloqueia)
        saveFlowToCache(flowId, normalizedFlow);
        
        return normalizedFlow;
      }
      return null;
    } catch (error) {
      console.error('❌ Erro ao carregar flow específico:', error);
      return null;
    }
  };

  // Última empresa/instante em que o resumo foi carregado, pra não refazer a
  // chamada a cada ida e volta na Home.
  const cardsResumoRef = useRef<{ empresaId: string; at: number } | null>(null);

  useEffect(() => {
    // 'flows': a lista de fluxos usa o mesmo resumo para "atrasados" e "entraram hoje"
    if (!['home', 'dashboards', 'organograma', 'flows'].includes(currentSection)) return;
    const empresaId = session?.empresa?.id;
    if (!empresaId) return;

    const ultimo = cardsResumoRef.current;
    if (ultimo && ultimo.empresaId === empresaId && Date.now() - ultimo.at < 60_000) return;

    cardsResumoRef.current = { empresaId, at: Date.now() };
    loadCardsResumo(session);
  }, [currentSection, session?.empresa?.id]);

  // A Home percorre flow.cards para montar os contadores dela; como a lista de
  // fluxos chega sem os cards, o resumo é reencaixado por flowId. Fluxo que já
  // tenha os cards completos carregados (o aberto no Kanban) fica como está.
  const flowsComCardsResumo = useMemo(() => {
    if (cardsResumo.length === 0) return flows;

    const porFlow = new Map<string, Card[]>();
    cardsResumo.forEach(card => {
      const flowId = String((card as any).flowId || '');
      if (!flowId) return;
      const lista = porFlow.get(flowId);
      if (lista) lista.push(card);
      else porFlow.set(flowId, [card]);
    });

    return flows.map(flow => {
      if (flow.cards && flow.cards.length > 0) return flow;
      const doFluxo = porFlow.get(String(flow.id));
      return doFluxo ? { ...flow, cards: doFluxo } : flow;
    });
  }, [flows, cardsResumo]);

  // Home e Dashboards analisam os cards de todos os fluxos, mas /api/flows passou
  // a responder com excludeCards=true (a resposta cheia chegava a 57MB e travava o
  // navegador). Aqui vem só o recorte que essas telas leem — ver /api/cards/resumo
  // no server.py. Carrega sob demanda, quando a pessoa abre uma dessas telas.
  const loadCardsResumo = async (activeSession: UserSession | null) => {
    const empresaId = activeSession?.empresa?.id;
    if (!empresaId) return;

    try {
      const userEmail = activeSession?.usuario?.email || '';
      const response = await fetch(
        `/api/cards/resumo?empresaId=${encodeURIComponent(empresaId)}&userEmail=${encodeURIComponent(userEmail)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);

      const data = await response.json();
      setCardsResumo(Array.isArray(data) ? data : []);
      console.log('✅ Resumo de cards carregado:', Array.isArray(data) ? data.length : 0);
    } catch (error) {
      console.error('❌ Erro ao carregar resumo de cards:', error);
      setCardsResumo([]);
    }
  };

  const loadFlows = async (activeSession: UserSession | null) => {
    if (!activeSession?.empresa?.id) return;
    
    setLoadingState('flows', true);
    try {
      const empresaId = activeSession.empresa.id;
      const currentUserEmail = activeSession?.usuario?.email || '';
      
      console.log('📊 Carregando fluxos (metadados apenas - carrega cards sob demanda)...');
      const response = await fetch(`/api/flows?userEmail=${encodeURIComponent(currentUserEmail)}&empresaId=${empresaId}&excludeCards=true`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);

      const result = await response.json();
      let flowsData = result.data || result;
      
      if (!Array.isArray(flowsData)) {
        flowsData = flowsData && typeof flowsData === 'object' ? [flowsData] : [];
      }
      
      let filteredFlows = filterByUserCompany(flowsData, activeSession, 'Fluxos');
      
      if (activeSession?.usuario?.role === 'viewer') {
        const userId = activeSession.usuario.id;
        filteredFlows = filteredFlows.filter((flow: any) => {
          const isCreator = flow.createdBy === userId || flow.responsavelId === userId;
          const isParticipant = flow.participantes?.includes(userId);
          const isStageParticipant = flow.etapas?.some((etapa: any) => 
            etapa.participantes?.includes(userId)
          );
          return isCreator || isParticipant || isStageParticipant;
        });
      }
      
      // 🔄 Mesclar flows: preservar o flow já carregado (com cards) se existir
      setFlows(prevFlows => {
        // Se já tem um flow carregado com cards, preservar ele
        const existingFlowWithCards = prevFlows.find(f => f.cards && f.cards.length > 0);
        if (existingFlowWithCards) {
          // Mesclar: usar o flow existente com cards, e adicionar os outros
          const newFlows = filteredFlows.map((f: Flow) => {
            const existingId = String(existingFlowWithCards.id || (existingFlowWithCards as any)._id);
            const newId = String(f.id || (f as any)._id);
            if (existingId === newId) {
              return existingFlowWithCards; // Preservar o flow com cards
            }
            return f;
          });
          console.log('✅ Fluxos mesclados (preservando cards):', newFlows.length);
          return newFlows;
        }
        return filteredFlows;
      });
      console.log('✅ Fluxos carregados:', filteredFlows.length);

      // Carregar config global de permissões de fluxos
      try {
        const configRes = await fetch(`/api/flows/config/permissoes?empresaId=${empresaId}`, {
          headers: {
            'X-User-Data': JSON.stringify({
              email: currentUserEmail,
              role: activeSession?.usuario?.role || 'admin',
              empresaId,
            })
          }
        });
        if (configRes.ok) {
          const configData = await configRes.json();
          setFlowGlobalConfig(configData.configs || []);
        }
      } catch (_) {}

      // Carregar informações do plano / trial
      try {
        const planoRes = await fetch(`/api/empresa/plano?empresaId=${empresaId}`, {
          headers: {
            'X-User-Data': JSON.stringify({
              email: currentUserEmail,
              role: activeSession?.usuario?.role || 'admin',
              empresaId,
            })
          }
        });
        if (planoRes.ok) {
          const planoData = await planoRes.json();
          if (planoData.success) {
            setPlanoInfo(planoData);
          }
        }
      } catch (_) {}
    } catch (error) {
      console.error('❌ Erro ao carregar fluxos:', error);
      setFlows([]);
    } finally {
      setLoadingState('flows', false);
    }
  };

  const loadColaboradores = async (activeSession: UserSession | null) => {
    if (!activeSession?.empresa?.id) return;
    
    setLoadingState('colaboradores', true);
    try {
      const currentUserEmail = activeSession?.usuario?.email || '';
      const empresaIdParam = encodeURIComponent(activeSession?.empresa?.id || activeSession?.usuario?.empresaId || '');
      
      console.log('👥 Carregando colaboradores...');
      const response = await fetch(`/api/colaboradores?userEmail=${encodeURIComponent(currentUserEmail)}&empresaId=${empresaIdParam}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          const colaboradoresMapeados = data.data.map((col: any) => ({
            ...col,
            id: col._id || col.id,
            userId: col.userId || col.usuarioId,
            createdAt: new Date(col.createdAt),
            updatedAt: new Date(col.updatedAt)
          }));
          const filteredColaboradores = filterByUserCompany(colaboradoresMapeados, activeSession, 'Colaboradores');
          setColaboradores(filteredColaboradores);
          console.log('✅ Colaboradores carregados:', filteredColaboradores.length);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar colaboradores:', error);
    } finally {
      setLoadingState('colaboradores', false);
    }
  };

  // ============================================================
  // TASKS - Carregamento e CRUD
  // ============================================================
  const normalizeHistorico = (historico?: any[]) =>
    historico?.map((h: any) => {
      const rawAcao = h.acao ?? h.tipo;
      const acaoMap: Record<string, TaskHistorico['acao']> = {
        mudanca_status: 'status_alterado',
        criacao: 'criada'
      };
      const realizadoEmRaw = h.realizadoEm ?? h.data;
      let realizadoEmStr = typeof realizadoEmRaw === 'string' ? realizadoEmRaw : undefined;
      if (realizadoEmStr && /\.\d{4,}/.test(realizadoEmStr)) {
        realizadoEmStr = realizadoEmStr.replace(/\.(\d{3})\d+/, '.$1');
      }
      const realizadoEmDate = realizadoEmStr
        ? new Date(realizadoEmStr)
        : realizadoEmRaw
          ? new Date(realizadoEmRaw)
          : undefined;

      return {
        ...h,
        acao: (acaoMap[rawAcao] || rawAcao) as TaskHistorico['acao'],
        realizadoPor: h.realizadoPor ?? h.usuarioId,
        realizadoEm: realizadoEmDate && !isNaN(realizadoEmDate.getTime()) ? realizadoEmDate : undefined
      };
    });

  const loadTasks = async (activeSession: UserSession | null) => {
    if (!activeSession?.empresa?.id) return;
    
    try {
      const empresaId = activeSession?.empresa?.id || activeSession?.usuario?.empresaId || '';
      
      console.log('📋 Carregando tasks...');
      // 🔒 Identidade vai junto: o backend só devolve as tarefas em que a pessoa
      // está envolvida (ou tudo, se for o admin master).
      const userEmail = activeSession?.usuario?.email || '';
      const response = await fetch(
        `/api/tasks?empresaId=${encodeURIComponent(empresaId)}&userEmail=${encodeURIComponent(userEmail)}`,
        {
          headers: {
            'X-User-Email': userEmail,
            'X-User-Id': String(activeSession?.usuario?.id || '')
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.tasks) {
          const tasksMapeadas = data.tasks.map((task: any) => ({
            ...task,
            criadaEm: new Date(task.createdAt),
            atualizadaEm: new Date(task.updatedAt),
            prazo: new Date(task.prazo),
            concluidaEm: task.concluidaEm ? new Date(task.concluidaEm) : undefined,
            historico: normalizeHistorico(task.historico),
            justificativaEm: task.justificativaEm ? new Date(task.justificativaEm) : undefined
          }));
          setTasks(tasksMapeadas);
          console.log('✅ Tasks carregadas:', tasksMapeadas.length);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tasks:', error);
    }
  };

  const handleCreateTask = async (
    task: Omit<Task, 'id' | 'criadaEm' | 'atualizadaEm' | 'historico' | 'nivelEscalonamentoAtual'>,
    arquivos?: File[]
  ) => {
    try {
      console.log('➕ Criando nova task:', task.titulo);
      
      const response = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: task.titulo,
          descricao: task.descricao,
          solicitanteId: task.solicitanteId,
          responsavelId: task.responsavelId,
          observadoresIds: task.observadoresIds,
          prazo: task.prazo,
          status: task.status || 'em_andamento', // ✅ Adicionado status
          prioridade: task.prioridade,
          flowId: task.flowId,
          cardId: task.cardId,
          empresaId: task.empresaId,
          tags: task.tags,
          camposConfig: task.camposConfig,
          camposValores: task.camposValores
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.task) {
          const novaTask = {
            ...data.task,
            criadaEm: new Date(data.task.createdAt),
            atualizadaEm: new Date(data.task.updatedAt),
            prazo: new Date(data.task.prazo),
            anexos: data.task.anexos || [],
            historico: normalizeHistorico(data.task.historico)
          };
          setTasks(prev => [novaTask, ...prev]);
          toast.success('Tarefa criada com sucesso!');

          // Se houver anexos selecionados, fazer upload agora (sem base64)
          if (arquivos && arquivos.length > 0) {
            try {
              const currentColaborador = colaboradores.find(c =>
                c.email === session?.usuario?.email || c.id === session?.usuario?.id
              );

              const formData = new FormData();
              arquivos.forEach(file => formData.append('arquivos', file));
              formData.append('enviadoPor', currentColaborador?.id || session?.usuario?.id || 'sistema');

              const uploadResp = await fetch(`/api/tasks/${novaTask.id}/anexos`, {
                method: 'POST',
                body: formData
              });

              if (uploadResp.ok) {
                const uploadData = await uploadResp.json();
                if (uploadData.status === 'success' && Array.isArray(uploadData.anexos)) {
                  setTasks(prev => prev.map(t => {
                    if (t.id !== novaTask.id) return t;
                    return {
                      ...t,
                      anexos: [...(t.anexos || []), ...uploadData.anexos]
                    };
                  }));
                  toast.success('Anexos enviados com sucesso!');
                }
              } else {
                toast.error('Tarefa criada, mas falhou ao enviar anexos');
              }
            } catch (e) {
              console.error('❌ Erro ao enviar anexos:', e);
              toast.error('Tarefa criada, mas falhou ao enviar anexos');
            }
          }
        }
      } else {
        toast.error('Erro ao criar tarefa');
      }
    } catch (error) {
      console.error('❌ Erro ao criar task:', error);
      toast.error('Erro ao criar tarefa');
    }
  };

  const handleUpdateTask = async (task: Task) => {
    try {
      console.log('✏️ Atualizando task:', task.id);
      
      const currentColaborador = colaboradores.find(c => 
        c.email === session?.usuario?.email || c.id === session?.usuario?.id
      );

      const response = await fetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: task.titulo,
          descricao: task.descricao,
          responsavelId: task.responsavelId,
          observadoresIds: task.observadoresIds,
          prazo: task.prazo,
          status: task.status,
          prioridade: task.prioridade,
          tags: task.tags,
          camposConfig: task.camposConfig,
          camposValores: task.camposValores,
          comentarios: task.comentarios,
          historico: task.historico,
          justificativaAtraso: task.justificativaAtraso,
          justificativaEm: task.justificativaEm,
          justificativaPor: task.justificativaPor,
          atualizadoPor: currentColaborador?.id || session?.usuario?.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.task) {
          const taskAtualizada = {
            ...data.task,
            criadaEm: new Date(data.task.createdAt),
            atualizadaEm: new Date(data.task.updatedAt),
            prazo: new Date(data.task.prazo),
            concluidaEm: data.task.concluidaEm ? new Date(data.task.concluidaEm) : undefined,
            historico: normalizeHistorico(data.task.historico),
            justificativaEm: data.task.justificativaEm ? new Date(data.task.justificativaEm) : undefined
          };
          setTasks(prev => prev.map(t => t.id === task.id ? taskAtualizada : t));
          toast.success('Tarefa atualizada com sucesso!');
        }
      } else {
        toast.error('Erro ao atualizar tarefa');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar task:', error);
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      console.log('🗑️ Deletando task:', taskId);
      
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        toast.success('Tarefa excluída com sucesso!');
      } else {
        toast.error('Erro ao excluir tarefa');
      }
    } catch (error) {
      console.error('❌ Erro ao deletar task:', error);
      toast.error('Erro ao excluir tarefa');
    }
  };

  const loadDepartamentos = async (activeSession: UserSession | null) => {
    if (!activeSession?.empresa?.id) return;
    
    setLoadingState('departamentos', true);
    try {
      const empresaId = activeSession?.empresa?.id || activeSession?.usuario?.empresaId || '';
      
      console.log('🏢 Carregando departamentos...');
      const response = await fetch(`/api/departamentos?empresaId=${encodeURIComponent(empresaId)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const departamentosMapeados = data.data.map((dept: any) => ({
            ...dept,
            id: dept._id || dept.id
          }));
          const filteredDepartamentos = filterByUserCompany(departamentosMapeados, activeSession, 'Departamentos');
          setDepartamentos(filteredDepartamentos);
          console.log('✅ Departamentos carregados:', filteredDepartamentos.length);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar departamentos:', error);
    } finally {
      setLoadingState('departamentos', false);
    }
  };

  const loadUsers = async (activeSession: UserSession | null) => {
    if (!activeSession) return;
    
    setLoadingState('users', true);
    try {
      console.log('👤 Carregando usuários...');
      const response = await fetch(`/api/users`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setUsers(data.data);
          console.log('✅ Usuários carregados:', data.data.length);
        } else if (data.users) {
          setUsers(data.users);
          console.log('✅ Usuários carregados (legacy):', data.users.length);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error);
    } finally {
      setLoadingState('users', false);
    }
  };

  const loadAutomacoes = async (activeSession: UserSession | null) => {
    if (!activeSession?.empresa?.id) return;
    
    setLoadingState('automacoes', true);
    try {
      const empresaId = activeSession?.empresa?.id || activeSession?.usuario?.empresaId || '';
      
      console.log('⚙️ Carregando automações...');
      const response = await fetch(`/api/automacoes?empresaId=${encodeURIComponent(empresaId)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const automacoesMapeadas = data.data.map((auto: any) => ({
            ...auto,
            id: auto._id || auto.id,
            createdAt: new Date(auto.createdAt),
            updatedAt: new Date(auto.updatedAt)
          }));
          const filteredAutomacoes = filterByUserCompany(automacoesMapeadas, activeSession, 'Automações');
          setAutomacoes(filteredAutomacoes);
          console.log('✅ Automações carregadas:', filteredAutomacoes.length);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar automações:', error);
    } finally {
      setLoadingState('automacoes', false);
    }
  };

  // 🚀 CARREGAMENTO PROGRESSIVO - Carrega dados em segundo plano sem bloquear a UI
  const loadDataProgressively = async (activeSession: UserSession | null) => {
    if (!activeSession?.empresa?.id) return;
    
    console.log('🚀 Iniciando carregamento progressivo em background...');
    showLoading('mongodb', 'Carregando dados', 'Buscando informações da empresa...');

    // Garantir que a empresa da sessão esteja no state de empresas
    if (activeSession.empresa) {
      setEmpresas(prev => {
        const exists = prev.some(e => e.id === activeSession.empresa.id);
        if (!exists) {
          console.log('🏢 Adicionando empresa da sessão ao state:', activeSession.empresa.nome);
          return [...prev, activeSession.empresa];
        }
        return prev;
      });
    }

    // Carregar empresas acessíveis (empresa principal + sub-empresas onde o usuário participa)
    // para TODOS os usuários, não só admins. O backend já escopa por participação (colaborador),
    // então editores/usuários veem apenas as empresas das quais realmente fazem parte.
    let empresasPromise: Promise<any> = Promise.resolve();
    if (activeSession?.usuario?.email) {
      empresasPromise = loadEmpresasFromBackend(activeSession.usuario.email, true).catch(err =>
        console.warn('⚠️ Não foi possível carregar empresas acessíveis:', err)
      );
    }
    
    // Carregar dados em paralelo sem bloquear
    Promise.all([
      loadFlows(activeSession),
      loadColaboradores(activeSession),
      loadDepartamentos(activeSession),
      loadUsers(activeSession),
      loadAutomacoes(activeSession),
      loadTasks(activeSession),
      empresasPromise
    ]).then(() => {
      console.log('✅ Todos os dados carregados!');
      updateMessage('Pronto!', 'Tudo carregado com sucesso.');
      hideLoading();
    }).catch(error => {
      console.error('❌ Erro no carregamento progressivo:', error);
      hideLoading();
    });
  };

  // Load data from MongoDB (LEGACY - mantido para compatibilidade, use loadDataProgressively)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadDataFromMongoDB = async (sessionOverride?: UserSession | null) => {
    console.log('🔄 Carregando dados do MongoDB...');
    const activeSession = sessionOverride ?? session;
    const currentUserEmail = activeSession?.usuario?.email || '';
    
    showLoading('mongodb', 'Conectando ao MongoDB', 'Estabelecendo conexão...');
    
    try {
      // ETAPA 1: Carregar empresas do backend (para admin, company_admin ou quando não há empresa na sessão)
      const isAdminUser = activeSession?.usuario?.role === 'administrator' || activeSession?.usuario?.role === 'admin';
      const hasEmpresaSelecionada = Boolean(activeSession?.empresa?.id);
      if (isAdminUser || !hasEmpresaSelecionada) {
        updateMessage('Carregando empresas', 'Buscando empresas no Atlas MongoDB...');

        try {
          const response = await fetch(`/api/empresas?userEmail=${encodeURIComponent(currentUserEmail)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const empresasResult = await response.json();
            if (empresasResult.success && empresasResult.data) {
              console.log('✅ Empresas carregadas do backend:', empresasResult.data);
              setEmpresas(empresasResult.data.map((empresa: any) => ({
                id: empresa._id,
                nome: empresa.nome || empresa.name || 'Empresa sem nome',
                cnpj: empresa.cnpj || '',
                razaoSocial: empresa.razaoSocial || '',
                email: empresa.email || '',
                telefone: empresa.telefone || '',
                endereco: empresa.endereco || {
                  rua: '',
                  numero: '',
                  complemento: '',
                  bairro: '',
                  cidade: '',
                  estado: '',
                  cep: ''
                },
                status: empresa.status || 'ativo',
                configuracoes: empresa.configuracoes || {},
                createdAt: empresa.createdAt ? new Date(empresa.createdAt) : new Date(),
                updatedAt: empresa.updatedAt ? new Date(empresa.updatedAt) : new Date()
              })));
            } else {
              console.warn('⚠️ Resposta do backend não contém empresas válidas');
            }
          } else if (response.status === 403) {
            console.warn('🚫 Sem permissão para listar empresas (403). Pulando etapa para usuário não admin.');
            // Não interromper o fluxo; seguirá com a empresa da sessão
          } else {
            console.warn('⚠️ Erro HTTP ao buscar empresas:', response.status);
          }
        } catch (empresasError) {
          console.error('❌ Erro ao carregar empresas do backend:', empresasError);
          // Modo estrito: sem fallback local. Propagar erro para tela fatal.
          throw empresasError;
        }
      } else {
        console.log('⏭️ Pulando carga de empresas: usuário não admin já tem empresa selecionada.');
      }

      // Usar activeSession que já foi passado como parâmetro
      if (activeSession?.empresa?.id) {
        const empresaId = activeSession.empresa.id;
        console.log('🏢 Empresa selecionada:', empresaId, '(tipo:', typeof empresaId, ')');
        
        if (empresaId) {
          // ETAPA 2: Carregar FLOWS do backend
          console.log('� ETAPA 2/5: Buscando fluxos do backend...');
          updateMessage('Carregando fluxos de trabalho', 'Obtendo fluxos da empresa...');
          
          await new Promise(resolve => setTimeout(resolve, 800)); // Mostrar progresso
          
          try {
            const response = await fetch(`/api/flows?userEmail=${encodeURIComponent(currentUserEmail)}&empresaId=${empresaId}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              throw new Error(`Erro HTTP: ${response.status}`);
            }

            const result = await response.json();
            console.log('📊 Resposta completa da API:', result);
            
            let flowsData = result;
            if (result.data) {
              flowsData = result.data;
            }
            
            console.log('📊 Fluxos recebidos do backend:', flowsData);
            console.log('📊 Tipo dos dados:', typeof flowsData, 'É array:', Array.isArray(flowsData));
            
            if (!Array.isArray(flowsData)) {
              console.error('❌ flowsData não é um array:', flowsData);
              console.log('🔄 Tentando usar flowsData como objeto único...');
              // Se não é array, mas é um objeto válido, transformar em array
              if (flowsData && typeof flowsData === 'object') {
                flowsData = [flowsData];
              } else {
                throw new Error('Dados de fluxos inválidos recebidos da API');
              }
            }
            
            let filteredFlows = filterByUserCompany(flowsData, activeSession, 'Fluxos');
            
            console.log('🔍 [FLOWS DEBUG]', {
              totalFlows: flowsData.length,
              afterCompanyFilter: filteredFlows.length,
              userRole: activeSession?.usuario?.role,
              userEmpresaId: activeSession?.usuario?.empresaId
            });
            
            // Viewers só podem ver fluxos onde participam
            // Editores e Administradores podem ver todos os fluxos da empresa
            if (activeSession?.usuario?.role === 'viewer') {
              const userId = activeSession.usuario.id;
              filteredFlows = filteredFlows.filter((flow: any) => {
                const isCreator = flow.createdBy === userId || flow.responsavelId === userId;
                const isParticipant = flow.participantes?.includes(userId);
                const isStageParticipant = flow.etapas?.some((etapa: any) => 
                  etapa.participantes?.includes(userId)
                );
                return isCreator || isParticipant || isStageParticipant;
              });
            }
            
            console.log('✅ [FLOWS FINAL]', {
              finalCount: filteredFlows.length,
              flowTitles: filteredFlows.map((f: any) => f.title)
            });
            
            console.log('🔍 Fluxos filtrados antes do setFlows:', filteredFlows);
            filteredFlows.forEach((flow: any, index: number) => {
              console.log(`🔍 Flow ${index + 1} - ${flow.nome}:`, flow);
              if (flow.cards && Array.isArray(flow.cards)) {
                console.log(`🔍 Cards do flow ${flow.nome}:`, flow.cards);
                flow.cards.forEach((card: any, cardIndex: number) => {
                  console.log(`🔍 Card ${cardIndex + 1} - ${card.titulo}:`, card.campos);
                });
              }
            });
            
            setFlows(filteredFlows);
            console.log('✅ Fluxos carregados do backend:', filteredFlows.length);
            
          } catch (error) {
            console.error('❌ Erro ao carregar fluxos:', error);
            setFlows([]);
          }

          // ETAPA 3: Carregar colaboradores
          console.log('👥 ETAPA 3/5: Carregando colaboradores...');
          updateMessage('Carregando colaboradores', 'Buscando dados dos colaboradores...');
          
          try {
            console.log('📥 Buscando colaboradores do MongoDB...');
            const empresaIdParam = encodeURIComponent(activeSession?.empresa?.id || activeSession?.usuario?.empresaId || '');
            const colaboradoresResponse = await fetch(`/api/colaboradores?userEmail=${encodeURIComponent(currentUserEmail)}&empresaId=${empresaIdParam}`);
            if (colaboradoresResponse.ok) {
              const colaboradoresData = await colaboradoresResponse.json();
              if (colaboradoresData.data) {
                // Mapear _id para id para compatibilidade com o frontend
                const colaboradoresMapeados = colaboradoresData.data.map((col: any) => ({
                  ...col,
                  id: col._id || col.id,
                  createdAt: new Date(col.createdAt),
                  updatedAt: new Date(col.updatedAt)
                }));
                // Aplicar filtro por empresa
                const filteredColaboradores = filterByUserCompany(colaboradoresMapeados, activeSession, 'Colaboradores');
                setColaboradores(filteredColaboradores);
                console.log('✅ Colaboradores carregados do MongoDB:', filteredColaboradores.length);
                
                // Log detalhado de TODOS os colaboradores para debug
                console.log('📋 LISTA COMPLETA DE COLABORADORES:');
                filteredColaboradores.forEach((col: any) => {
                  console.log(`  - ${col.nome} (${col.email}):`, {
                    id: col.id,
                    _id: col._id
                  });
                });
              } else if (colaboradoresData.colaboradores) {
                const filteredColaboradores = filterByUserCompany(colaboradoresData.colaboradores, activeSession, 'Colaboradores');
                setColaboradores(filteredColaboradores);
                console.log('✅ Colaboradores carregados do MongoDB (legacy):', filteredColaboradores.length);
              }
            } else {
              // Modo estrito: sem fallback local
              throw new Error('Falha ao buscar colaboradores no backend');
            }
          } catch (error) {
            console.error('⚠️ Erro ao carregar colaboradores', error);
            throw error;
          }

          // ETAPA 4: Carregar departamentos
          updateMessage('Carregando departamentos', 'Obtendo estrutura organizacional...');

          try {
            const empresaId = activeSession?.empresa?.id || activeSession?.usuario?.empresaId || '';
            const departamentosResponse = await fetch(`/api/departamentos?empresaId=${encodeURIComponent(empresaId)}`);
            if (departamentosResponse.ok) {
              const departamentosData = await departamentosResponse.json();
              if (departamentosData.success && departamentosData.data) {
                // Mapear _id para id para compatibilidade com o frontend
                const departamentosMapeados = departamentosData.data.map((dept: any) => ({
                  ...dept,
                  id: dept._id || dept.id
                }));
                // Aplicar filtro por empresa
                const filteredDepartamentos = filterByUserCompany(departamentosMapeados, activeSession, 'Departamentos');
                setDepartamentos(filteredDepartamentos);
                console.log('✅ Departamentos carregados do MongoDB:', filteredDepartamentos.length);
              }
            } else {
              // Modo estrito: sem fallback local
              throw new Error('Falha ao buscar departamentos no backend');
            }
          } catch (error) {
            console.error('⚠️ Erro ao carregar departamentos', error);
            throw error;
          }

          // ETAPA 5: Carregar usuários e configurações
          console.log('⚙️ ETAPA 5/5: Carregando usuários e configurações...');
          updateMessage('Finalizando carregamento', 'Carregando usuários e configurações...');

          try {
            console.log('📥 Buscando TODOS os usuários do MongoDB...');
            // NÃO passar userEmail - precisamos de TODOS os users para mapear aos colaboradores
            const usersResponse = await fetch(`/api/users`);
            if (usersResponse.ok) {
              const usersData = await usersResponse.json();
              if (usersData.success && usersData.data) {
                // NÃO filtrar users por empresa - users são contas do sistema que podem acessar múltiplas empresas
                // O matching colaborador<->user é feito por email no componente Colaboradores
                setUsers(usersData.data);
                console.log('✅ Usuários carregados do MongoDB:', usersData.data.length);
              } else if (usersData.users) {
                setUsers(usersData.users);
                console.log('✅ Usuários carregados do MongoDB (legacy):', usersData.users.length);
              }
            } else {
              // Modo estrito: sem fallback local
              throw new Error('Falha ao buscar usuários no backend');
            }
          } catch (error) {
            console.error('⚠️ Erro ao carregar usuários', error);
            throw error;
          }

          // ETAPA EXTRA: Carregar automações
          console.log('⚙️ ETAPA EXTRA: Carregando automações...');
          updateMessage('Carregando automações', 'Buscando automações configuradas...');

          try {
            console.log('📥 Buscando automações do MongoDB...');
            const empresaId = activeSession?.empresa?.id || activeSession?.usuario?.empresaId || '';
            const automacoesResponse = await fetch(`/api/automacoes?empresaId=${encodeURIComponent(empresaId)}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            if (automacoesResponse.ok) {
              const automacoesData = await automacoesResponse.json();
              if (automacoesData.success && automacoesData.data) {
                // Mapear _id para id para compatibilidade com o frontend
                const automacoesMapeadas = automacoesData.data.map((auto: any) => ({
                  ...auto,
                  id: auto._id || auto.id,
                  createdAt: new Date(auto.createdAt),
                  updatedAt: new Date(auto.updatedAt)
                }));
                // Aplicar filtro por empresa
                const filteredAutomacoes = filterByUserCompany(automacoesMapeadas, activeSession, 'Automações');
                console.log('🔍 [DEBUG] Automações recebidas do backend:', filteredAutomacoes);
                console.log('🔍 [DEBUG] Primeira automação:', filteredAutomacoes[0]);
                setAutomacoes(filteredAutomacoes);
                console.log('✅ Automações carregadas do MongoDB:', filteredAutomacoes.length);
              }
            } else {
              // Modo estrito: sem fallback local
              throw new Error('Falha ao buscar automações no backend');
            }
          } catch (error) {
            console.error('⚠️ Erro ao carregar automações', error);
            throw error;
          }

          // Sem fallback: somente dados do backend
        }
      }
      
      // Aguardar um pouco antes de finalizar para mostrar que está completo
      updateMessage('Carregamento concluído', 'Todos os dados foram carregados com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados do MongoDB (modo estrito):', error);
      setFatalError('Não foi possível carregar dados do servidor. Verifique Backend e Banco de Dados.');
    } finally {
      hideLoading();
    }
  };
  

  // Carregar dados iniciais (OTIMIZADO - apenas limpeza)
  useEffect(() => {
    console.log('🚀 Inicializando aplicação...');
    cleanDuplicatesFromStorage();
    cleanupOldCaches(); // 🧹 Limpar caches de flows antigos do IndexedDB
    
    // 🧹 Migração: Limpar localStorage antigo (migramos para IndexedDB)
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('escalone_flow_cache_')) {
          keysToRemove.push(key);
        }
      }
      if (keysToRemove.length > 0) {
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('🧹 Cache antigo do localStorage limpo:', keysToRemove.length, 'items');
      }
    } catch (e) { /* ignore */ }
    
    // 🔗 Detectar URL /c/:shortCode e salvar para processar após carregamento
    const urlPath = window.location.pathname;
    const shortCodeMatch = urlPath.match(/^\/c\/([A-Fa-f0-9]{6,7})$/);
    if (shortCodeMatch && shortCodeMatch[1]) {
      const shortCode = shortCodeMatch[1].toUpperCase();
      console.log('🔗 ShortCode detectado na URL:', shortCode);
      
      // Timeout de segurança - máximo 10 segundos no loading
      const timeoutId = setTimeout(() => {
        console.warn('⏰ Timeout ao carregar card via shortCode');
        setLoadingShortCode(false);
      }, 10000);
      
      // Buscar card pelo shortCode e salvar para abrir depois
      fetch(`/api/cards/by-shortcode/${shortCode}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            console.log('✅ Card resolvido do shortCode:', data);
            // Salvar no sessionStorage ANTES de limpar a URL
            sessionStorage.setItem('pendingCardOpen', JSON.stringify({
              flowId: data.flowId,
              cardId: data.cardId
            }));
            // Também usar estado para processar imediatamente
            setPendingShortCodeCard({
              flowId: data.flowId,
              cardId: data.cardId
            });
            // Limpar a URL DEPOIS de salvar os dados
            window.history.replaceState({}, '', '/');
          } else {
            console.warn('⚠️ Card não encontrado para shortCode:', shortCode);
            clearTimeout(timeoutId);
            setLoadingShortCode(false);
            window.history.replaceState({}, '', '/');
          }
        })
        .catch(err => {
          console.error('Erro ao resolver shortCode:', err);
          clearTimeout(timeoutId);
          setLoadingShortCode(false);
          window.history.replaceState({}, '', '/');
        });
    }

    // 🔗 Verificar se é uma URL de tarefa /t/:taskId
    const taskMatch = urlPath.match(/^\/t\/(.+)$/);
    if (taskMatch && taskMatch[1]) {
      const taskId = taskMatch[1];
      console.log('📋 Tarefa detectada na URL:', taskId);
      sessionStorage.setItem('pendingTaskOpen', taskId);
      window.history.replaceState({}, '', '/');
    }
    
    // 🔗 Timeout de segurança para URL /flows/:flowId - máximo 5 segundos no loading (otimizado)
    const flowMatch = urlPath.match(/^\/flows\/([a-f0-9]{24})(?:\/card\/([A-Fa-f0-9]{6,7}))?$/);
    if (flowMatch && flowMatch[1]) {
      console.log('🔗 Flow detectado na URL:', flowMatch[1], flowMatch[2] ? `com card ${flowMatch[2]}` : '');
      
      const flowTimeoutId = setTimeout(() => {
        console.warn('⏰ Timeout ao carregar fluxo via URL');
        setLoadingFlowUrl(false);
        setPendingFlowUrl(null);
      }, 5000); // 5 segundos - otimizado para carregamento prioritário
      
      // Limpar timeout quando o flow for aberto (via useEffect que processa pendingFlowUrl)
      // Salvamos o timeoutId no sessionStorage para poder limpar depois
      (window as any).__flowUrlTimeoutId = flowTimeoutId;
    }
  }, []);

  // Detectar F5 e restaurar sessão (OTIMIZADO - COM CARREGAMENTO PROGRESSIVO)
  useEffect(() => {
    const startTime = performance.now();
    console.log('🔍 [T+0ms] Verificando sessão armazenada...');
    
    // Sempre restaurar sessão do localStorage
    const storedSession = localStorage.getItem('session');
    if (storedSession) {
      const parsedSession = JSON.parse(storedSession);
      setSession(parsedSession);
      console.log(`✅ [T+${Math.round(performance.now() - startTime)}ms] Sessão restaurada:`, parsedSession.usuario?.email);

      // 🔄 O nome guardado aqui é o do momento do login. Quem renomeia um
      // colaborador esperava ver o nome novo, mas a sessão nunca reconsultava o
      // servidor — e ninguém desloga por meses. Reconfere em segundo plano e
      // corrige só o que mudou; se a chamada falhar, mantém o que já estava
      // (ninguém pode ser deslogado por causa disso).
      const emailSessao = parsedSession.usuario?.email;
      if (emailSessao) {
        fetch(`${getApiBase()}/usuario-atual?email=${encodeURIComponent(emailSessao)}`)
          .then(r => (r.ok ? r.json() : null))
          .then(dados => {
            // Nome E perfil: trocar alguém de Consulta para Editor não chegava em
            // quem já estava logado — o "Adicionar Cartão" seguia escondido.
            const nomeAtual = dados?.usuario?.nome || parsedSession.usuario?.nome;
            const roleServidor = dados?.usuario?.role;
            const roleAtual = ['administrator', 'admin', 'editor', 'viewer'].includes(roleServidor)
              ? roleServidor
              : parsedSession.usuario?.role;
            if (nomeAtual === parsedSession.usuario?.nome && roleAtual === parsedSession.usuario?.role) return;
            console.log(`🔄 Sessão atualizada: nome "${parsedSession.usuario?.nome}" -> "${nomeAtual}", perfil "${parsedSession.usuario?.role}" -> "${roleAtual}"`);
            const sessaoAtualizada = {
              ...parsedSession,
              usuario: { ...parsedSession.usuario, nome: nomeAtual, role: roleAtual },
            };
            setSession(sessaoAtualizada);
            try { localStorage.setItem('session', JSON.stringify(sessaoAtualizada)); } catch {}
          })
          .catch(() => { /* offline ou servidor fora: segue com a sessão guardada */ });
      }
      
      // � VERIFICAR PARÂMETROS DE DEPARTAMENTO NA URL ANTES DE QUALQUER REDIRECIONAMENTO
      const urlParams = new URLSearchParams(location.search);
      const departamentoId = urlParams.get('d');
      
      if (departamentoId) {
        console.log('🔗 Departamento detectado na URL durante restauração:', departamentoId);
        setCurrentSection('departamentos');
        // Carregar dados em background, mas NÃO mudar a seção
        setTimeout(() => {
          loadDataProgressively(parsedSession);
        }, 100);
        return; // Sair aqui para não executar a lógica de fluxos
      }
      
      // �🚀 Verificar se há fluxo pendente na URL para priorizar carregamento
      const urlPath = window.location.pathname;
      console.log(`🔗 [T+${Math.round(performance.now() - startTime)}ms] URL detectada:`, urlPath);
      
      // Também verificar sessionStorage (fallback quando Vite redireciona para /)
      const savedFlowUrl = sessionStorage.getItem('pendingFlowUrl');
      console.log(`💾 [T+${Math.round(performance.now() - startTime)}ms] Flow salvo no sessionStorage:`, savedFlowUrl);
      
      // Regex mais flexível para capturar o flow ID
      let flowMatch = urlPath.match(/\/flows\/([a-f0-9]{24})(?:\/card\/([A-Fa-f0-9]{6,7}))?/);
      
      // Se não encontrou na URL, tentar do sessionStorage
      if (!flowMatch && savedFlowUrl) {
        flowMatch = savedFlowUrl.match(/\/flows\/([a-f0-9]{24})(?:\/card\/([A-Fa-f0-9]{6,7}))?/);
        console.log(`🔄 [T+${Math.round(performance.now() - startTime)}ms] Usando flowMatch do sessionStorage:`, flowMatch);
      }
      
      console.log(`🔍 [T+${Math.round(performance.now() - startTime)}ms] flowMatch final:`, flowMatch);
      
      // Limpar sessionStorage após usar
      if (savedFlowUrl) {
        sessionStorage.removeItem('pendingFlowUrl');
      }
      
      if (flowMatch && flowMatch[1]) {
        const targetFlowId = flowMatch[1];
        const targetCardShortCode = flowMatch[2];
        
        // ⚡ CARREGAMENTO ULTRA-RÁPIDO: Carregar APENAS o flow específico!
        console.log(`⚡ [T+${Math.round(performance.now() - startTime)}ms] Modo TURBO: Carregando flow`, targetFlowId);
        
        // SEM setTimeout - executar imediatamente!
        (async () => {
          try {
            console.log(`🌐 [T+${Math.round(performance.now() - startTime)}ms] Iniciando fetch...`);
            const flow = await loadSingleFlow(targetFlowId);
            console.log(`📦 [T+${Math.round(performance.now() - startTime)}ms] Fetch concluído`);
            
            if (flow) {
              console.log(`🚀 [T+${Math.round(performance.now() - startTime)}ms] Abrindo fluxo:`, flow.name);
              
              // Adicionar ao estado de flows e abrir
              setFlows([flow]);
              setSelectedFlow(flow);
              setCurrentSection('flows');
              
              // Limpar timeout de segurança
              if ((window as any).__flowUrlTimeoutId) {
                clearTimeout((window as any).__flowUrlTimeoutId);
                delete (window as any).__flowUrlTimeoutId;
              }
              
              console.log(`✅ [T+${Math.round(performance.now() - startTime)}ms] Fluxo aberto! Carregando dados em background...`);
              
              // Carregar o resto em background (não bloqueia)
              Promise.all([
                loadFlows(parsedSession), // Carregar os outros flows
                loadColaboradores(parsedSession),
                loadDepartamentos(parsedSession),
                loadUsers(parsedSession),
                loadAutomacoes(parsedSession)
              ]).then(() => {
                console.log('✅ Todos os dados carregados em background!');
              });
            } else {
              // Flow não encontrado - carregar tudo normalmente
              console.warn('⚠️ Flow não encontrado, carregando normalmente...');
              loadDataProgressively(parsedSession);
            }
          } catch (error) {
            console.error('❌ Erro ao carregar fluxo via URL:', error);
            loadDataProgressively(parsedSession);
          } finally {
            // 🔒 GARANTIR que o loading seja sempre resetado
            setLoadingFlowUrl(false);
            setPendingFlowUrl(null);
          }
        })();
      } else {
        // Carregamento normal (sem fluxo na URL)
        console.log('🔄 Iniciando carregamento progressivo em background');
        setTimeout(() => {
          loadDataProgressively(parsedSession);
        }, 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vazio = executa APENAS uma vez na montagem

  // 🔗 Verificar se há um card na URL e abrir automaticamente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cardId = params.get('card');
    
    // Verificar também se há card pendente do shortCode (sessionStorage ou estado)
    const pendingCardStr = sessionStorage.getItem('pendingCardOpen');
    const pendingCard = pendingCardStr ? JSON.parse(pendingCardStr) : pendingShortCodeCard;
    
    // Usar card da URL ou card pendente
    const targetCardId = cardId || pendingCard?.cardId;
    const targetFlowId = pendingCard?.flowId;
    
    // Só processar se tiver card, sessão ativa e flows carregados
    if (!targetCardId || !session || flows.length === 0) {
      if (targetCardId && session && flows.length === 0) {
        console.log('⏳ Aguardando flows carregarem para abrir card:', targetCardId);
      }
      return;
    }
    
    console.log('🔗 Card detectado para abrir:', targetCardId);
    console.log('📊 Total de flows disponíveis:', flows.length);
    console.log('🎯 Buscando flow:', targetFlowId, 'card:', targetCardId);
    
    // Limpar card pendente
    sessionStorage.removeItem('pendingCardOpen');
    if (pendingShortCodeCard) {
      setPendingShortCodeCard(null);
    }
    
    // Encontrar o flow pelo flowId (prioridade) ou buscando nos cards
    let flowComCard = targetFlowId 
      ? flows.find(flow => {
          const flowId = String(flow.id || (flow as any)._id);
          const match = flowId === String(targetFlowId);
          console.log(`  Comparando flow ${flowId} com ${targetFlowId}: ${match}`);
          return match;
        })
      : flows.find(flow => flow.cards?.some(c => String(c.id) === String(targetCardId)));
    
    if (flowComCard) {
      console.log('✅ Flow encontrado:', flowComCard.name, '- ID:', flowComCard.id);
      console.log('✅ Navegando para o flow e abrindo card:', targetCardId);
      
      // Navegar para o flow
      setSelectedFlow(flowComCard);
      setCurrentSection('flows');
      
      // Definir o card para ser aberto (usar o cardId real, não shortCode)
      setCardToOpen(targetCardId);
      
      // Desligar loading do shortCode (card vai abrir)
      setLoadingShortCode(false);
      
      // Limpar o parâmetro da URL
      window.history.replaceState({}, '', '/');
    } else {
      console.warn('⚠️ Flow não encontrado. Flows disponíveis:');
      flows.forEach(f => console.log(`  - ${f.name} (id: ${f.id}, _id: ${(f as any)._id})`));
      
      // Se não encontrou no estado local mas temos flowId, pode ser que os flows não carregaram completamente
      if (targetFlowId) {
        console.log('🔄 Flow não encontrado, aguardando próximo ciclo...');
        // Não limpar o pendingShortCodeCard ainda - deixar para próxima tentativa
      } else {
        // Sem flowId e card não encontrado - desligar loading
        setLoadingShortCode(false);
      }
    }
  }, [session, flows, pendingShortCodeCard]);

  // 🔗 Verificar se há tarefa pendente para abrir (via /t/:taskId)
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
  useEffect(() => {
    const pendingTask = sessionStorage.getItem('pendingTaskOpen');
    if (pendingTask && session && tasks.length > 0) {
      sessionStorage.removeItem('pendingTaskOpen');
      console.log('📋 Tarefa pendente para abrir:', pendingTask);
      setPendingTaskId(pendingTask);
      setCurrentSection('tasks');
    } else if (pendingTask && session && tasks.length === 0) {
      console.log('⏳ Aguardando tasks carregarem para abrir tarefa:', pendingTask);
    }
  }, [session, tasks]);

  // Limpar pendingTaskId após ser consumido pelo componente Tasks
  const handleTaskOpened = () => {
    setPendingTaskId(null);
  };

  // 🔗 Verificar se há um flow na URL (/flows/:flowId) e abrir automaticamente no F5
  useEffect(() => {
    // Só processar se tiver flowId pendente, sessão ativa e flows carregados
    if (!pendingFlowUrl || !session || flows.length === 0) {
      if (pendingFlowUrl && session && flows.length === 0) {
        console.log('⏳ Aguardando flows carregarem para abrir fluxo:', pendingFlowUrl.flowId);
      }
      return;
    }
    
    console.log('🔗 Flow detectado na URL:', pendingFlowUrl.flowId);
    console.log('📊 Total de flows disponíveis:', flows.length);
    
    // Limpar timeout de segurança
    if ((window as any).__flowUrlTimeoutId) {
      clearTimeout((window as any).__flowUrlTimeoutId);
      delete (window as any).__flowUrlTimeoutId;
    }
    
    // Encontrar o flow pelo flowId
    const flowEncontrado = flows.find(flow => {
      const flowId = String(flow.id || (flow as any)._id);
      const match = flowId === String(pendingFlowUrl.flowId);
      console.log(`  Comparando flow ${flowId} com ${pendingFlowUrl.flowId}: ${match}`);
      return match;
    });
    
    if (flowEncontrado) {
      console.log('✅ Flow encontrado via URL:', flowEncontrado.name);
      
      // 🚀 CACHE: Usar loadSingleFlow que verifica cache primeiro
      const targetFlowId = String(flowEncontrado.id || (flowEncontrado as any)._id);
      
      // Tentar carregar do cache (instantâneo) ou do servidor
      loadSingleFlow(targetFlowId).then(flowCompleto => {
        if (flowCompleto) {
          setSelectedFlow(flowCompleto);
          setCurrentSection('flows');
          console.log('✅ Flow aberto (cache ou servidor):', flowCompleto.name);
        } else {
          // Fallback: usar o flow da lista
          setSelectedFlow(flowEncontrado);
          setCurrentSection('flows');
        }
        
        // Desligar loading
        setLoadingFlowUrl(false);
      });
      
      // Se tinha um card shortCode na URL, preparar para abrir o card
      if (pendingFlowUrl.cardShortCode) {
        console.log('🎯 Card shortCode na URL:', pendingFlowUrl.cardShortCode);
        // O KanbanBoard vai detectar e abrir o card automaticamente via URL
      }
      
      // Limpar o pendingFlowUrl
      setPendingFlowUrl(null);
      
      // Não limpar a URL - deixar o KanbanBoard gerenciar
      // Se não tinha card, limpa para /flows/:flowId (KanbanBoard vai fazer isso)
    } else {
      console.warn('⚠️ Flow não encontrado para URL. Flows disponíveis:');
      flows.forEach(f => console.log(`  - ${f.name} (id: ${f.id}, _id: ${(f as any)._id})`));
      
      // Timeout de segurança - se não encontrou após flows carregarem
      setPendingFlowUrl(null);
      setLoadingFlowUrl(false);
      window.history.replaceState({}, '', '/');
    }
  }, [session, flows, pendingFlowUrl]);

  // 🔗 Verificar se há um procedimento na URL (/procedimento/:departamentoId/:procedimentoId)
  useEffect(() => {
    if (!pendingProcedimentoUrl || !session) {
      return;
    }
    
    console.log('📋 Procedimento detectado na URL:', pendingProcedimentoUrl);
    
    // Navegar para a seção de procedimentos com o departamento filtrado
    setSelectedDepartamentoId(pendingProcedimentoUrl.departamentoId);
    setCurrentSection('procedimentos');
    
    // Salvar o ID do procedimento para o componente abrir automaticamente
    sessionStorage.setItem('pendingProcedimentoId', pendingProcedimentoUrl.procedimentoId);
    
    // Limpar estados
    setPendingProcedimentoUrl(null);
    setLoadingProcedimentoUrl(false);
    
    // Atualizar URL para não recarregar ao dar F5
    window.history.replaceState({}, '', '/procedimentos');
    
  }, [session, pendingProcedimentoUrl]);

  // 🔗 Salvar URL do fluxo no sessionStorage para recuperar após F5
  // Isso é necessário porque o Vite pode redirecionar para / antes do React carregar
  useEffect(() => {
    if (selectedFlow && currentSection === 'flows') {
      const flowUrl = `/flows/${selectedFlow.id}`;
      sessionStorage.setItem('pendingFlowUrl', flowUrl);
      console.log('💾 URL do fluxo salva no sessionStorage:', flowUrl);
    } else {
      // Se saiu do fluxo, remover do sessionStorage
      sessionStorage.removeItem('pendingFlowUrl');
    }
  }, [selectedFlow, currentSection]);

  // 🔐 Carregar permissões granulares do fluxo (para checkEtapaEditPermission etc.)
  useEffect(() => {
    if (!selectedFlow?.id) {
      setFlowPermissoes([]);
      return;
    }
    fetch(`/api/flows/${selectedFlow.id}/permissoes`)
      .then(res => res.json())
      .then(data => {
        if (data?.permissoes && Array.isArray(data.permissoes)) {
          setFlowPermissoes(data.permissoes);
        } else {
          setFlowPermissoes([]);
        }
      })
      .catch(() => setFlowPermissoes([]));
  }, [selectedFlow?.id]);

  // Removido: login automático de teste (modo estrito)

  // Desabilitar login automático via URL e sessão padrão; exigir autenticação manual
  useEffect(() => {
    // Se não está autenticado, nada é feito aqui; as rotas cuidam do redirecionamento para /login
    // Se desejar suporte a magic-link futuramente, adicionar validação de token aqui.
  }, [session]);

  // Função para limpar duplicatas do localStorage
  const cleanDuplicatesFromStorage = () => {
    const keys = ['flows', 'departamentos', 'colaboradores', 'procedimentos', 'automacoes', 'users'];
    
    keys.forEach(key => {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          const uniqueData = removeDuplicates(data);
          if (data.length !== uniqueData.length) {
            console.log(`Removidas ${data.length - uniqueData.length} duplicatas de ${key}`);
            localStorage.setItem(key, JSON.stringify(uniqueData));
          }
        } catch (error) {
          console.error(`Erro ao limpar duplicatas de ${key}:`, error);
        }
      }
    });
  };

  // 🚀 Recarregar dados quando trocar de empresa (PROGRESSIVO)
  useEffect(() => {
    if (session?.empresa?.id) {
      console.log('🔄 Empresa alterada - recarregando dados progressivamente');
      loadDataProgressively(session);
    }
  }, [session?.empresa?.id]);

  // 🔄 Sincronização automática: recarregar dados quando a janela voltar ao foco
  useEffect(() => {
    if (!session?.empresa?.id) return;

    let lastFocusTime = Date.now();
    let isReloading = false; // Flag para prevenir múltiplas execuções simultâneas
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isReloading) {
        const timeSinceLastFocus = Date.now() - lastFocusTime;
        // Se passou mais de 5 segundos desde o último foco, recarregar apenas flows (mais leve)
        if (timeSinceLastFocus > 5000) {
          console.log('🔄 Aba voltou ao foco - recarregando flows via loadFlows(session)...');
          isReloading = true;
          // Recarregar flows usando loadFlows que já aplica filtro de empresa correto
          const reloadFlows = async () => {
            try {
              await loadFlows(session);
              console.log('✅ Flows recarregados com filtro de empresa correto');
            } catch (error) {
              console.error('⚠️ Erro ao recarregar flows:', error);
            } finally {
              setTimeout(() => {
                isReloading = false;
              }, 1000);
            }
          };
          reloadFlows();
        }
        lastFocusTime = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session?.empresa?.id]);

  // ✅ CORREÇÃO REMOVIDA: Polling automático removido para evitar loading constante
  // Para atualizar manualmente, use F5 ou clique em "Atualizar"

  // Save data (mantém os dados de outras empresas)
  const saveData = (key: string, newData: any[]) => {
    const stored = localStorage.getItem(key);
    const existingData = stored ? JSON.parse(stored) : [];
    
    const empresaId = session?.empresa?.id;
    if (!empresaId) {
      // Remover duplicatas globalmente se não há empresa específica
      const uniqueData = removeDuplicates(newData);
      localStorage.setItem(key, JSON.stringify(uniqueData));
      return;
    }

    // Remove dados antigos da empresa atual
    const otherCompaniesData = existingData.filter((item: any) => 
      item.empresaId !== empresaId
    );

    // Adiciona os novos dados (sem duplicatas)
    const uniqueNewData = removeDuplicates(newData);
    const dataToSave = [...otherCompaniesData, ...uniqueNewData];
    
    // Remover duplicatas finais por precaução
    const finalData = removeDuplicates(dataToSave);
    
    localStorage.setItem(key, JSON.stringify(finalData));
  };

  // ✅ CORREÇÃO: Função para refresh manual dos dados
  /* const handleRefreshData = () => {
    console.log('🔄 Refresh manual solicitado...');
    showLoading('mongodb', 'Atualizando dados', 'Carregando informações mais recentes...');
    loadDataFromMongoDB();
  }; */

  // User Management Functions
  const handleUpdateUser = async (userId: string, userData: { senha?: string; role?: UserRole; status?: 'ativo' | 'inativo'; empresasPermitidas?: string[]; permissoes?: any }) => {
    try {
      const currentUserEmail = session?.usuario?.email || '';
      const response = await fetch(`/api/users/${userId}?userEmail=${encodeURIComponent(currentUserEmail)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          updatedAt: new Date()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao atualizar usuário no servidor');
      }

      console.log('✅ Usuário atualizado no MongoDB:', userData);

      // 2. Atualizar no estado local só após sucesso no backend
      const updatedUsers = users.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            ...userData,
            updatedAt: new Date()
          };
        }
        return user;
      });
      
      setUsers(updatedUsers);
      
      // 3. Atualizar no localStorage como backup
      const allUsers = JSON.parse(localStorage.getItem('users') || '[]');
      const updatedAllUsers = allUsers.map((user: User) => {
        if (user.id === userId) {
          return {
            ...user,
            ...userData,
            updatedAt: new Date()
          };
        }
        return user;
      });
      
      localStorage.setItem('users', JSON.stringify(updatedAllUsers));
      
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao atualizar usuário:', error);
      throw error;
    }
  };

  const handleCreateUser = (userData: {
    id: string;
    nome: string;
    email: string;
    senha: string;
    role: UserRole;
    departamentoId?: string;
    status: 'ativo' | 'inativo';
    empresaId: string;
    empresasPermitidas?: string[];
  }) => {
    const newUser: User = {
      ...userData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: userData.status, 
      verificadoEm: new Date() // Usuário já verificado (criado pelo admin)
    };
    
    const updatedUsers = [...users, newUser];
    setUsers(updatedUsers);
    
    // Atualizar no localStorage completo
    const allUsers = JSON.parse(localStorage.getItem('users') || '[]');
    localStorage.setItem('users', JSON.stringify([...allUsers, newUser]));
  };

  const handleSignUp = async (userData: {
    nome: string;
    email: string;
    senha: string;
  }) => {
    try {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      if (users.some((user: User) => user.email === userData.email)) {
        throw new Error('Já existe um usuário com este email');
      }
  
      const codigoVerificacao = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      const newUser: User = {
        id: Date.now().toString(),
        nome: userData.nome,
        email: userData.email,
        senha: userData.senha,
        role: 'viewer',
        status: 'pendente',
        createdAt: new Date(),
        updatedAt: new Date()
      };
  
      try {
        const response = await fetch(`${getApiBase()}/enviar-verificacao`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userData.email,
            nome: userData.nome,
            codigo: codigoVerificacao
          })
        });

        if (!response.ok) {
          throw new Error('Falha ao enviar email de verificação');
        }

        localStorage.setItem('users', JSON.stringify([...users, newUser]));
        setVerificacaoEmail({ email: userData.email, codigo: codigoVerificacao });

      } catch (emailError) {
        console.error('Erro ao enviar email:', emailError);
        throw new Error('Não foi possível enviar o email de verificação');
      }
  
    } catch (error) {
      console.error('Erro no cadastro:', error);
      throw error;
    }
  };
  
  const handlePaymentSuccess = async (email: string) => {
    try {
      // Conta já criada pelo Asaas — faz login direto
      await handleLogin({ email, senha: '' });
    } catch (error) {
      console.error('Erro ao fazer login após pagamento:', error);
      // Fallback: redireciona para login com email preenchido
      navigate(`/login?email=${encodeURIComponent(email)}&fromPayment=1`);
    }
  };

  const handleVerificarEmail = async (codigoInformado: string) => {
    if (!verificacaoEmail) return;

    if (codigoInformado.trim().toUpperCase() !== verificacaoEmail.codigo.trim().toUpperCase()) {
      alert('Código de verificação inválido');
      return;
    }

    // Tentar ativar conta no MongoDB (fluxo trial)
    try {
      const res = await fetch(`${getApiBase()}/ativar-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificacaoEmail.email, codigo: codigoInformado.trim().toUpperCase() }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const senhaParaLogin = verificacaoEmail.senha || '';
        setVerificacaoEmail(null);
        await handleLogin({ email: verificacaoEmail.email, senha: senhaParaLogin });
        return;
      }
      if (!data.success) {
        alert(data.error || 'Erro ao ativar conta');
        return;
      }
    } catch (err) {
      console.warn('Fallback para verificação local:', err);
    }

    // Fallback: fluxo legado (localStorage)
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    const userIndex = users.findIndex((u: User) => u.email === verificacaoEmail.email);
    if (userIndex >= 0) {
      users[userIndex].status = 'ativo';
      users[userIndex].verificadoEm = new Date();
      localStorage.setItem('users', JSON.stringify(users));
      handleLogin({
        email: verificacaoEmail.email,
        senha: users[userIndex].senha,
      });
    }
  };

  // Função para verificar permissões com o backend
  const verificarPermissoes = async (email: string) => {
    try {
      // Permissões baseadas no role do usuário (vindo do banco via login)
      console.log('🔍 Verificando permissões para:', email);
      
      // Buscar role da sessão atual
      const sessionData = JSON.parse(localStorage.getItem('session') || '{}');
      const userRole = sessionData?.usuario?.role || '';
      const isAdmin = ['admin', 'administrator', 'global_admin'].includes(userRole.toLowerCase());
      
      const permissions = {
        canEdit: true,
        canAdmin: isAdmin,
        currentUser: {
          email: email,
          nome: sessionData?.usuario?.nome || email.split('@')[0],
          isAdmin: isAdmin
        }
      };
      
      console.log('✅ Permissões definidas:', permissions);
      return permissions;
      
      /* Código original comentado até implementar o endpoint
      const response = await fetch('/api/admin/verificar-acesso', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        const data = await response.json();
        return data.permissions;
      }
      */
    } catch (error) {
      console.error('Erro ao verificar permissões:', error);
    }
    return null;
  };

  const handleLogin = async (credentials: LoginCredentials) => {
    try {
      console.log('🔐 Iniciando login via MongoDB Atlas...');

      // Limpar state da sessão anterior para evitar mostrar dados de outra empresa
      setFlows([]);
      setColaboradores([]);
      setDepartamentos([]);
      
      // Buscar usuário pelo email (já foi validado no componente Login)
      const usersResponse = await fetch(`/api/users?userEmail=${encodeURIComponent(credentials.email)}`);
      
      if (!usersResponse.ok) {
        throw new Error(`Erro ao buscar usuário: ${usersResponse.status}`);
      }
      
      const usersData = await usersResponse.json();
      let users = [];
      if (usersData.success && usersData.data) {
        users = usersData.data;
      } else if (usersData.users) {
        users = usersData.users;
      }
      
      if (users.length === 0) {
        throw new Error('Usuário não encontrado');
      }
      
      const user: User = {
        ...users[0],
        id: users[0]._id || users[0].id,
        createdAt: users[0].createdAt ? new Date(users[0].createdAt) : new Date(),
        updatedAt: users[0].updatedAt ? new Date(users[0].updatedAt) : new Date()
      };
      
      console.log('✅ Usuário autenticado:', user.email, '- Departamento:', user.departamentoId);

      // Verificar permissões no backend
      const permissions = await verificarPermissoes(user.email);
      if (permissions) {
        console.log('🔐 Permissões carregadas para ${user.email}:', permissions);
      }
      
      // Caso especial: Login direto com empresaId (vindo de convite)
      if (credentials.empresaId) {
        const empresa = empresas.find(e => e.id === credentials.empresaId);
        if (!empresa) {
          throw new Error('Empresa não encontrada');
        }
        
        // Criar sessão com empresa selecionada
        const userSession: UserSession = {
          usuario: user,
          empresa
        };
        
        setSession(userSession);
        localStorage.setItem('session', JSON.stringify(userSession));
        
        // 🚀 Carregar dados em background após login
        setTimeout(() => loadDataProgressively(userSession), 100);
        
        navigate('/');
        return;
      }
      
      // ✅ NOVO: Se o usuário já tem empresaId no registro, usar direto
      if (user.empresaId) {
        console.log('🏢 Usuário tem empresaId no registro:', user.empresaId);

        // Buscar dados reais da empresa no backend
        let empresa: Empresa;
        try {
          const empRes = await fetch(`/api/empresas/${user.empresaId}`);
          if (empRes.ok) {
            const empData = await empRes.json();
            const e = empData.data || empData;
            empresa = {
              id: e._id || user.empresaId,
              nome: e.nome || user.nomeEmpresa || 'Minha Empresa',
              cnpj: e.cnpj || '',
              razaoSocial: e.razaoSocial || '',
              email: e.email || user.email || '',
              telefone: e.telefone || '',
              endereco: e.endereco || { rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', cep: '' },
              status: e.status || 'ativo',
              configuracoes: e.configuracoes || {},
              createdAt: e.createdAt ? new Date(e.createdAt) : new Date(),
              updatedAt: e.updatedAt ? new Date(e.updatedAt) : new Date()
            };
          } else {
            throw new Error('Empresa não encontrada');
          }
        } catch {
          empresa = {
            id: user.empresaId,
            nome: user.nomeEmpresa || user.nome || 'Minha Empresa',
            cnpj: '',
            razaoSocial: user.nomeEmpresa || '',
            email: user.email || '',
            telefone: '',
            endereco: { rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', cep: '' },
            status: 'ativo',
            createdAt: new Date(),
            updatedAt: new Date()
          };
        }

        // Garantir que o role admin vem do banco
        if (!user.role || user.role === 'viewer') {
          user.role = 'admin';
        }
        
        const userSession: UserSession = {
          usuario: user,
          empresa
        };
        
        console.log('✅ Sessão criada com empresa do registro do usuário');
        setSession(userSession);
        localStorage.setItem('session', JSON.stringify(userSession));

        // Adicionar empresa ao state de empresas para o componente Empresas exibir
        setEmpresas([empresa]);
        localStorage.setItem('empresas', JSON.stringify([empresa]));
        
        // 🚀 Carregar dados em background após login
        setTimeout(() => loadDataProgressively(userSession), 100);
        
        navigate('/');
        return;
      }

      // Carregar empresas baseado nas permissões do usuário (apenas para admin)
      console.log('🔍 Carregando empresas para usuário:', user.email);
      const empresasCarregadas = await loadEmpresasFromBackend(user.email);
      console.log('📊 Empresas carregadas do backend:', empresasCarregadas);
      
      // Filtrar empresas que o usuário tem acesso (agora vem do backend com permissões)
      let empresasAcessiveis = empresasCarregadas || [];
      
      console.log(`Usuário ${user.nome} tem acesso a ${empresasAcessiveis.length} empresas:`, 
                 empresasAcessiveis.map((e: Empresa) => e.nome));
  
      // Escolher próximos passos com base no número de empresas acessíveis
      if (empresasAcessiveis.length === 1) {
        // Se há apenas uma empresa, selecionar automaticamente
        const empresa = empresasAcessiveis[0];
        console.log('🏢 [LOGIN] Empresa selecionada automaticamente:', empresa);
        console.log('🔑 [LOGIN] Empresa ID:', empresa?.id, 'tipo:', typeof empresa?.id);
        
        const userSession: UserSession = {
          usuario: user,
          empresa
        };
        
        console.log('📦 [LOGIN] Sessão criada:', {
          usuario: user.nome,
          usuarioId: user.id,
          empresa: empresa.nome,
          empresaId: empresa.id
        });
        
        setSession(userSession);
        localStorage.setItem('session', JSON.stringify(userSession));
        
        // 🚀 Carregar dados em background após login
        setTimeout(() => loadDataProgressively(userSession), 100);
        
        navigate('/');
      } else if (empresasAcessiveis.length > 1 || user.role === 'administrator') {
        // Se há múltiplas empresas ou é admin, mostrar tela de seleção
        console.log('🏢 Mostrando tela de seleção de empresa');
        console.log('📋 Empresas disponíveis para seleção:', empresasAcessiveis.length);
        
        // Aguardar um ciclo de renderização para garantir que setEmpresas foi processado
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setTempUser(user);
        setStep('empresa');
      } else {
        // Se não há empresas acessíveis
        throw new Error('Você não tem acesso a nenhuma empresa. Entre em contato com o administrador.');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    }
  };

  const handleLogout = () => {
  console.log('🚪 Usuário fazendo logout explícito');
    setSession(null);
    setSelectedProduct(null);
    setShowUserMenu(false);
    setSelectedFlow(null);
    setCurrentSection('home');
    setExpandedMenus(['organizacoes']);
    setVerificacaoEmail(null);
    setTempUser(null);
    setStep('credentials');
    setPlanoInfo(null);
    setShowPlanosScreen(false);
    setShowOnboarding(false);
    
    localStorage.removeItem('session');
    localStorage.removeItem('selectedProduct');
    navigate('/login');
  };

  // 🏦 Handler para seleção de produto
  const handleSelectProduct = (product: 'escalone' | 'finance' | 'atlas') => {
    setSelectedProduct(product);
    localStorage.setItem('selectedProduct', product);
  };

  const handleVoltarParaSelecao = () => {
    setSelectedProduct(null);
    localStorage.removeItem('selectedProduct');
  };

  // Função para redefinição de senha
  const handleResetPassword = async (email: string, token: string, newPassword: string) => {
    try {
      // Verificar token e redefinir senha no servidor
      const verifyResponse = await fetch(`${getApiBase()}/redefinir-senha`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, token, novaSenha: newPassword })
      });

      if (!verifyResponse.ok) {
        const data = await verifyResponse.json();
        throw new Error(data.error || 'Código de verificação inválido');
      }

      // Sucesso - senha atualizada no MongoDB pelo backend
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      throw error;
    }
  };

  // Navigation handlers
  const toggleSubMenu = (menuId: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Handlers para dados
  const handleEmpresasChange = async (newEmpresas: Empresa[]) => {
    console.log('🔄 handleEmpresasChange chamado com', newEmpresas.length, 'empresas');
    
    // Determinar qual empresa foi modificada/adicionada
    const empresaModificada = newEmpresas.find(empresa => {
      const empresaExistente = empresas.find(e => e.id === empresa.id);
      const isModified = !empresaExistente || JSON.stringify(empresa) !== JSON.stringify(empresaExistente);
      if (isModified) {
        console.log('📝 Empresa modificada detectada:', empresa.nome, '(ID:', empresa.id, ')');
      }
      return isModified;
    });

    if (empresaModificada) {
      try {
        // Verificar se é uma empresa nova ou uma atualização
        // Uma empresa é considerada nova se o ID é um timestamp (número) ou se não existe no estado atual
        const empresaExistente = empresas.find(e => e.id === empresaModificada.id);
        const isNewEmpresa = !empresaExistente || /^\d+$/.test(empresaModificada.id.toString());
        
        console.log('🔍 Análise da empresa:', {
          nome: empresaModificada.nome,
          id: empresaModificada.id,
          isNewEmpresa,
          empresaExistente: !!empresaExistente
        });
        
        if (isNewEmpresa) {
          // Criar nova empresa no MongoDB
          console.log('📤 Criando nova empresa no MongoDB:', empresaModificada.nome);
          const response = await fetch('/api/empresas', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Email': session?.usuario?.email || '',
            },
            body: JSON.stringify({
              nome: empresaModificada.nome,
              cnpj: empresaModificada.cnpj,
              razaoSocial: empresaModificada.razaoSocial,
              email: empresaModificada.email,
              telefone: empresaModificada.telefone,
              endereco: empresaModificada.endereco,
              status: empresaModificada.status,
              createdAt: new Date(),
              updatedAt: new Date()
            })
          });

          if (response.ok) {
            const result = await response.json();
            console.log('✅ Empresa criada no MongoDB:', result);
            
            // Atualizar o estado com o ID retornado pelo MongoDB
            const novoId = result.data?._id || result.data?.id || result._id || result.id || empresaModificada.id;
            const empresasAtualizadas = newEmpresas.map(emp => 
              emp.id === empresaModificada.id 
                ? { ...emp, id: novoId } 
                : emp
            );
            setEmpresas(empresasAtualizadas);
            localStorage.setItem('empresas', JSON.stringify(empresasAtualizadas));
            return; // Retorna aqui para não executar o resto
          } else {
            console.error('❌ Erro ao criar empresa no MongoDB');
            throw new Error('Falha ao salvar no servidor');
          }
        } else {
          // Atualizar empresa existente no MongoDB
          console.log('📤 Atualizando empresa no MongoDB:', empresaModificada.nome, 'ID:', empresaModificada.id);
          const response = await fetch(`/api/empresas/${empresaModificada.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Email': session?.usuario?.email || '',
            },
            body: JSON.stringify({
              nome: empresaModificada.nome,
              cnpj: empresaModificada.cnpj,
              razaoSocial: empresaModificada.razaoSocial,
              email: empresaModificada.email,
              telefone: empresaModificada.telefone,
              endereco: empresaModificada.endereco,
              status: empresaModificada.status,
              updatedAt: new Date()
            })
          });

          if (response.ok) {
            const result = await response.json();
            console.log('✅ Empresa atualizada no MongoDB:', result);
          } else {
            console.error('❌ Erro ao atualizar empresa no MongoDB:', response.status, response.statusText);
            const errorText = await response.text();
            console.error('❌ Resposta do servidor:', errorText);
            throw new Error(`Falha ao salvar no servidor: ${response.status}`);
          }
        }
      } catch (error) {
        console.error('❌ Erro ao salvar empresa:', error);
        alert('Erro ao salvar empresa no servidor. As alterações foram salvas localmente.');
      }
    }

    // Sempre atualizar o estado local
    console.log('💾 Atualizando estado local...');
    setEmpresas(newEmpresas);
    localStorage.setItem('empresas', JSON.stringify(newEmpresas));
    console.log('✅ handleEmpresasChange concluído');
  };

  // Nova função para carregar empresas do backend
  const loadEmpresasFromBackend = async (userEmail: string = '', silent: boolean = false) => {
    console.log('🏢 Carregando empresas do backend para usuário:', userEmail);
    if (!silent) {
      showLoading('empresas', 'Carregando empresas', 'Conectando ao backend...');
    }

    try {
      console.log('📨 Enviando requisição com headers:', {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      });

      const response = await fetch(`/api/empresas?userEmail=${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail
        }
      });

      console.log('📥 Resposta recebida:', response.status, response.statusText);

      if (response.ok) {
        const empresasResult = await response.json();
        console.log('📋 Dados completos da resposta:', empresasResult);

        if (empresasResult.success && empresasResult.data) {
          console.log('✅ Empresas carregadas do backend:', empresasResult.data.length, 'empresas');

          const empresasFormatadas = empresasResult.data.map((empresa: any) => ({
            id: empresa._id,
            nome: empresa.nome || empresa.name || 'Empresa sem nome',
            cnpj: empresa.cnpj || '',
            razaoSocial: empresa.razaoSocial || '',
            email: empresa.email || '',
            telefone: empresa.telefone || '',
            endereco: empresa.endereco || {
              rua: '',
              numero: '',
              complemento: '',
              bairro: '',
              cidade: '',
              estado: '',
              cep: ''
            },
            status: empresa.status || 'ativo',
            configuracoes: empresa.configuracoes || {},
            createdAt: empresa.createdAt ? new Date(empresa.createdAt) : new Date(),
            updatedAt: empresa.updatedAt ? new Date(empresa.updatedAt) : new Date()
          }));

          setEmpresas(empresasFormatadas);
          localStorage.setItem('empresas', JSON.stringify(empresasFormatadas));
          return empresasFormatadas;
        } else {
          console.warn('⚠️ Resposta do backend não contém empresas válidas');
          return null;
        }
      } else if (response.status === 403) {
        console.warn('🚫 Sem permissão para listar empresas (403). Retornando lista vazia.');
        setEmpresas([]);
        return [];
      } else {
        console.error('❌ Resposta não OK ao carregar empresas:', response.status);
        return null;
      }
    } catch (error) {
      console.error('❌ Erro ao carregar empresas do backend:', error);
      return null;
    } finally {
      if (!silent) {
        hideLoading();
      }
    }
  };

  const handleDepartamentosChange = (newDepartamentos: Departamento[]) => {
    const empresaId = session?.empresa?.id;
    if (!empresaId) return;

    const departamentosComEmpresa = newDepartamentos.map(dep => ({
      ...dep,
      empresaId: empresaId
    }));

    setDepartamentos(departamentosComEmpresa);
    saveData('departamentos', departamentosComEmpresa);
  };

  const handleColaboradoresChange = (newColaboradores: Colaborador[]) => {
    const empresaId = session?.empresa?.id;
    if (!empresaId) return;

    const colaboradoresComEmpresa = newColaboradores.map(col => ({
      ...col,
      empresaId: empresaId
    }));

    setColaboradores(colaboradoresComEmpresa);
    // Não salvar no localStorage - dados vêm do backend
  };

  const handleAutomacoesChange = async (newAutomacoes: Automacao[]) => {
    const empresaId = session?.empresa?.id;
    if (!empresaId) return;

    const automacoesComEmpresa = newAutomacoes.map(auto => ({
      ...auto,
      empresaId: empresaId,
      updatedAt: new Date()
    }));

    // 🔑 Só consideramos "alterada" uma automação cujo conteúdo realmente mudou
    // em relação ao estado anterior. Sem isso, editar UMA automação dispara PUT
    // em TODAS as 91 da empresa — incluindo órfãs/fora do escopo do editor, que
    // retornam 403 e abortam o save inteiro.
    const semVolatil = (a: any) => {
      const { updatedAt: _u, empresaId: _e, ...resto } = a || {};
      return JSON.stringify(resto);
    };
    const anteriorPorId = new Map(automacoes.map(a => [a.id, semVolatil(a)]));
    const foiAlterada = (auto: Automacao) => {
      const antes = anteriorPorId.get(auto.id);
      return antes === undefined || antes !== semVolatil(auto);
    };

  try {
      // Verificar automações excluídas
      const automacoesExcluidas = automacoes.filter(
        existing => !newAutomacoes.find(newAuto => newAuto.id === existing.id)
      );

      // Excluir automações removidas
      for (const automacaoExcluida of automacoesExcluidas) {
        // Incluir dados do usuário para passar na verificação de permissões do backend
        const userHeader = {
          email: (session as any)?.usuario?.email || (session as any)?.user?.email || (session as any)?.email || 'admin@local',
          role: (session as any)?.usuario?.role || (session as any)?.user?.role || (session as any)?.role || 'admin',
          empresaId: (session as any)?.empresa?.id,
          permissoes: (session as any)?.usuario?.permissoes
        };
        const response = await fetch(`/api/automacoes/${automacaoExcluida.id}`, {
          method: 'DELETE',
          headers: {
            'X-User-Data': JSON.stringify(userHeader)
          }
        });
        
        if (!response.ok) {
          console.warn(`Erro ao excluir automação ${automacaoExcluida.id}`);
        }
      }

      // Processar automações novas/atualizadas
      const automacoesProcessadas: Automacao[] = [];
      for (const automacao of automacoesComEmpresa) {
        const isNew = !automacoes.find(a => a.id === automacao.id);

        // Pula automações existentes que não mudaram: evita PUT desnecessário
        // em automações fora do escopo do editor (que retornariam 403).
        if (!isNew && !foiAlterada(automacao)) {
          automacoesProcessadas.push(automacao);
          continue;
        }

        console.log('🔍 Processando automação:', {
          id: automacao.id,
          nome: automacao.nome,
          isNew,
          existingIds: automacoes.map(a => a.id)
        });
        
        if (isNew) {
          // Criar nova automação
          console.log('➕ Criando nova automação:', automacao.nome);
          // NUNCA envie _id no body
          const { _id: _ignoredCreateId, ...payloadCreate } = (automacao as any);
          const userHeader = {
            email: (session as any)?.usuario?.email || (session as any)?.user?.email || (session as any)?.email || 'admin@local',
            role: (session as any)?.usuario?.role || (session as any)?.user?.role || (session as any)?.role || 'admin',
            empresaId: (session as any)?.empresa?.id,
            permissoes: (session as any)?.usuario?.permissoes
          };
          const response = await fetch('/api/automacoes', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Data': JSON.stringify(userHeader)
            },
            body: JSON.stringify({
              ...payloadCreate,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            })
          });
          
          if (!response.ok) {
            throw new Error('Erro ao criar automação');
          }
          // Usar o _id retornado pelo MongoDB como id definitivo
          const created = await response.json();
          const novoId = created?.data?._id;
          const automacaoPersistida = {
            ...automacao,
            id: novoId || automacao.id,
          } as Automacao;
          automacoesProcessadas.push(automacaoPersistida);
        } else {
          // Atualizar automação existente
          console.log('🔄 Atualizando automação existente:', automacao.nome);
          console.log('📋 Dados completos da automação:', automacao);
          console.log('🎯 Ações da automação:', automacao.acoes);
          
          // NUNCA envie _id no body
          const { _id: _ignoredUpdateId, ...payloadUpdate } = (automacao as any);
          const userHeader = {
            email: (session as any)?.usuario?.email || (session as any)?.user?.email || (session as any)?.email || 'admin@local',
            role: (session as any)?.usuario?.role || (session as any)?.user?.role || (session as any)?.role || 'admin',
            empresaId: (session as any)?.empresa?.id,
            permissoes: (session as any)?.usuario?.permissoes
          };
          const response = await fetch(`/api/automacoes/${automacao.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Data': JSON.stringify(userHeader)
            },
            body: JSON.stringify({
              ...payloadUpdate,
              updatedAt: new Date().toISOString()
            })
          });
          
          const responseData = await response.json();
          console.log('📤 Resposta da API PUT:', responseData);
          
          if (!response.ok) {
            throw new Error('Erro ao atualizar automação');
          }
          automacoesProcessadas.push(automacao);
        }
      }

      console.log('✅ Automações salvas no MongoDB');
      // Garantir estado consistente com IDs válidos do MongoDB
      setAutomacoes(automacoesProcessadas);
      return;
    } catch (error) {
      console.error('❌ Erro ao salvar automações no MongoDB:', error);
      // Fallback para localStorage
      saveData('automacoes', automacoesComEmpresa);
    }
    // Fallback: ainda assim atualiza estado local
    setAutomacoes(automacoesComEmpresa);
  };

  // 👑 Admin master: único isento da config global de fluxos (o toggle vale para todos os demais,
  // inclusive administradores da empresa)
  const isAdminMaster = () => ehAdminMaster(session?.usuario?.email);

  // Resolve o id usado como chave na config global (fluxos_global_config usa o id do colaborador)
  const getColaboradorIdAtual = () => {
    const userId = String(session?.usuario?.id || '');
    const colaborador = colaboradores.find(c =>
      String(c.id || (c as any)._id) === userId ||
      c.email === session?.usuario?.email
    );
    return colaborador ? String(colaborador.id || (colaborador as any)._id) : userId;
  };

  const podeCriarFluxos = (): boolean => {
    if (isAdminMaster()) return true;
    const conf = flowGlobalConfig.find(c => c.colaboradorId === getColaboradorIdAtual());
    // Se não há config específica, default é true (pode criar)
    return conf ? conf.podeCriarFluxos !== false : true;
  };

  const handleCreateFlow = async (name: string) => {
    console.log('🚀 handleCreateFlow chamado com nome:', name);

    const empresaId = session?.empresa?.id;
    if (!empresaId) {
      console.error('❌ Sem empresa selecionada');
      return;
    }

    // Verificar permissão de criação de fluxos da config global
    if (!podeCriarFluxos()) {
      console.warn('🚫 Usuário sem permissão para criar fluxos');
      alert('Seu usuário não tem permissão para criar fluxos.');
      return;
    }

    try {
      const newFlowData = {
        name,
        departamentoId: '',
        responsavelId: '',
        participantes: [],
        etapas: [],
        cards: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'ativo' as const,
        empresaId: empresaId,
        createdBy: session?.usuario?.id, // Rastrear quem criou o fluxo
        criadoPorNome: session?.usuario?.nome, // Nome do criador
        criadoPorEmail: session?.usuario?.email // Usado pelo backend para validar a config global
      };

      console.log('📤 Enviando dados para MongoDB:', newFlowData);

      // Criar o fluxo no MongoDB via API
      const { mongoService } = await import('./utils/mongoService');
      const createdFlow = await mongoService.createFlow(newFlowData);

      console.log('✅ Fluxo criado no MongoDB:', createdFlow);

      // Atualizar o estado local
      const updatedFlows = [...flows, createdFlow];
      setFlows(updatedFlows);
      
      console.log('✅ Estado local atualizado. Total de fluxos:', updatedFlows.length);
    } catch (error: any) {
      console.error('❌ Erro ao criar fluxo no MongoDB:', error);
      // 403 = bloqueado pela config global de fluxos (não é servidor fora do ar)
      if (String(error?.message || '').includes('403')) {
        alert('Seu usuário não tem permissão para criar fluxos.');
      } else {
        alert('Erro ao criar fluxo. Verifique se o servidor está funcionando.');
      }
    }
  };

  const handleUpdateFlow = async (updatedFlow: Flow): Promise<Flow> => {
    console.log('🔄 handleUpdateFlow chamado para:', updatedFlow.name);
    console.log('📊 Etapas:', updatedFlow.etapas?.length || 0);
    console.log('🃏 Cards:', updatedFlow.cards?.length || 0);
    
    const empresaId = session?.empresa?.id;
    if (!empresaId) {
      console.error('❌ EmpresaId não encontrado na sessão');
      return updatedFlow; // Retornar flow original se não tiver empresa
    }

    try {
      // Preparar dados para envio - sem normalização excessiva
      const flowToUpdate = {
        ...updatedFlow,
        empresaId: empresaId,
        updatedAt: new Date()
      };
      
      // Remover _id se existir (não deve ser enviado no update)
      const cleanFlow = { ...flowToUpdate };
      if ('_id' in cleanFlow) {
        delete (cleanFlow as any)._id;
      }
      
      console.log('📤 Enviando para MongoDB:', {
        id: flowToUpdate.id,
        name: flowToUpdate.name,
        etapas: flowToUpdate.etapas?.length || 0,
        cards: flowToUpdate.cards?.length || 0
      });

      if (cleanFlow.cards) {
        const automacoesDoFlow = automacoes.filter(auto => auto.flowId === cleanFlow.id);
        
        cleanFlow.cards.forEach(card => {
          automacoesDoFlow.forEach(automacao => {
            if (card.etapaId === automacao.etapaOrigemId && automacao.condicoes) {
              const todasCondicoesAtendidas = automacao.condicoes.every(condicao => {
                const campo = card.campos?.[condicao.campoId];
                
                switch (condicao.tipo) {
                  case 'preenchido':
                    return campo !== undefined && campo !== '' && campo !== null;
                  case 'valor_igual':
                    return campo === condicao.valor;
                  case 'valor_maior':
                    return Number(campo) > Number(condicao.valor);
                  case 'valor_menor':
                    return Number(campo) < Number(condicao.valor);
                  default:
                    return false;
                }
              });

              if (todasCondicoesAtendidas && automacao.etapaDestinoId) {
                console.log('🤖 Automação movendo card:', {
                  titulo: card.titulo,
                  de: card.etapaId,
                  para: automacao.etapaDestinoId,
                  automacao: automacao.nome
                });
                
                // Adicionar ao histórico de etapas COM camposSnapshot para preservar anexos
                const etapaHistory = card.etapaHistory || [];
                etapaHistory.push({
                  etapaId: String(automacao.etapaDestinoId),
                  movedAt: new Date(),
                  movedBy: session?.usuario?.id,
                  motivo: 'automacao',
                  automacaoId: automacao.id,
                  automacaoNome: automacao.nome,
                  responsavelId: card.responsavelIndividualId, // Registra responsável atual no histórico
                  // 🔧 FIX 23/01/2026: SALVAR camposSnapshot com anexos para histórico
                  camposSnapshot: { ...(card.campos || {}) }
                });
                
                card.etapaId = automacao.etapaDestinoId;
                card.etapaHistory = etapaHistory;
                card.updatedAt = new Date();
              }
            }
          });
        });
      }

      // Atualizar no MongoDB via API
      console.log('📤 Enviando dados para MongoDB:', {
        name: cleanFlow.name,
        etapas: cleanFlow.etapas?.length || 0,
        cards: cleanFlow.cards?.length || 0
      });
      
      // 🔍 DEBUG: Log detalhado das etapas que serão enviadas
      console.log('📋 [DEBUG] Etapas que serão enviadas ao MongoDB:');
      cleanFlow.etapas?.forEach((etapa, idx) => {
        console.log(`   Etapa [${idx}] ${etapa.nome}: participantes = ${etapa.participantes?.length || 0}`, etapa.participantes);
      });
      
      const { mongoService } = await import('./utils/mongoService');
      const updatedFlowFromAPI = await mongoService.updateFlow(cleanFlow.id, cleanFlow);

      console.log('📥 Resposta do MongoDB:', {
        id: updatedFlowFromAPI.id,
        name: updatedFlowFromAPI.name,
        etapas: updatedFlowFromAPI.etapas?.length || 0,
        cards: updatedFlowFromAPI.cards?.length || 0
      });
      
      // 🔍 DEBUG: Log detalhado das etapas retornadas
      console.log('📋 [DEBUG] Etapas retornadas do MongoDB:');
      updatedFlowFromAPI.etapas?.forEach((etapa, idx) => {
        console.log(`   Etapa [${idx}] ${etapa.nome}: participantes = ${etapa.participantes?.length || 0}`, etapa.participantes);
      });

      // Atualizar o estado local
      const updatedFlows = flows.map(f => f.id === updatedFlow.id ? updatedFlowFromAPI : f);
      setFlows(updatedFlows);
      setSelectedFlow(updatedFlowFromAPI);
      
      // 💾 Atualizar cache IndexedDB com o flow atualizado (inclui novas etapas)
      const flowId = updatedFlowFromAPI.id || (updatedFlowFromAPI as any)._id;
      if (flowId) {
        saveFlowToCache(flowId, updatedFlowFromAPI);
        console.log('💾 Cache IndexedDB atualizado com flow:', updatedFlowFromAPI.name);
      }
      
      console.log('✅ Fluxo atualizado com sucesso no MongoDB e estado local atualizado');
      console.log('🔄 Estado flows atualizado:', updatedFlows.length);
      console.log('🔄 SelectedFlow atualizado:', {
        id: updatedFlowFromAPI.id,
        name: updatedFlowFromAPI.name,
        cards: updatedFlowFromAPI.cards?.length || 0
      });
      
      return updatedFlowFromAPI;
    } catch (error) {
      console.error('❌ Erro ao atualizar fluxo no MongoDB:', error);
      
      // Só mostrar alert se for um erro real de conexão/API
      if (error instanceof Error && error.message.includes('API Error')) {
        // Recusa com motivo (sem permissão, etapa ainda com cards...) vem em "error":
        // mostrar o motivo em vez de culpar a conexão.
        let motivo = '';
        try {
          const corpo = error.message.slice(error.message.indexOf('): ') + 3);
          motivo = JSON.parse(corpo)?.error || '';
        } catch { /* corpo não-JSON: erro de conexão de verdade */ }
        alert(motivo || 'Erro de conexão com o servidor. Verifique se o backend está funcionando.');
      } else {
        console.warn('⚠️ Erro na atualização do fluxo, mas pode ter sido bem-sucedida:', error);
      }
      
      // Retornar o flow mesmo com erro (estado local já foi atualizado)
      return updatedFlow;
    }
  };

  const handleDeleteFlow = async (flowId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este fluxo? Esta ação não pode ser desfeita.')) {
      try {
        // Deletar do MongoDB via API
        const { mongoService } = await import('./utils/mongoService');
        await mongoService.deleteFlow(flowId);

        // Atualizar o estado local
        const updatedFlows = flows.filter(f => f.id !== flowId);
        setFlows(updatedFlows);
        
        const updatedAutomacoes = automacoes.filter(a => a.flowId !== flowId);
        setAutomacoes(updatedAutomacoes);
        
        if (selectedFlow?.id === flowId) {
          setSelectedFlow(null);
        }

        console.log('Fluxo deletado com sucesso do MongoDB');
      } catch (error) {
        console.error('Erro ao deletar fluxo do MongoDB:', error);
        alert('Erro ao deletar fluxo. Verifique se o servidor está funcionando.');
      }
    }
  };

  // Handler dedicado para criar fluxo a partir de template pré-definido (sem clonar automações/conexões)
  const handleCreateFromTemplate = async (templateData: any) => {
    try {
      showLoading('fluxos', 'Criando fluxo', 'Montando fluxo a partir do template...');
      const empresaId = session?.empresa?.id;
      if (!empresaId) {
        alert('Selecione uma empresa antes de criar um fluxo.');
        hideLoading();
        return;
      }

      // Verificar permissão de criação de fluxos da config global
      if (!podeCriarFluxos()) {
        hideLoading();
        alert('Seu usuário não tem permissão para criar fluxos.');
        return;
      }

      const genId = () => String(Date.now()) + Math.random().toString(36).slice(2, 8);

      // Mapear IDs antigos de etapa para novos
      const etapaIdMap = new Map<string, string>();
      const campoIdMapByEtapa = new Map<string, Map<string, string>>();
      // Índice global de campo (id do template -> id novo). Necessário porque
      // automações e camposVisiveisNoCard podem referenciar campos de outra etapa.
      const campoIdMapGlobal = new Map<string, string>();

      (templateData.etapas || []).forEach((etapa: any) => {
        const newEtapaId = genId();
        etapaIdMap.set(String(etapa.id), newEtapaId);
        const campoMap = new Map<string, string>();
        (etapa.campos || []).forEach((campo: any) => {
          const novoCampoId = genId();
          campoMap.set(String(campo.id), novoCampoId);
          campoIdMapGlobal.set(String(campo.id), novoCampoId);
        });
        campoIdMapByEtapa.set(String(etapa.id), campoMap);
      });

      const mapEtapas = (ids?: string[]) =>
        (ids || []).map((id: string) => etapaIdMap.get(String(id))).filter(Boolean) as string[];
      const mapCampos = (ids?: string[]) =>
        (ids || []).map((id: string) => campoIdMapGlobal.get(String(id))).filter(Boolean) as string[];

      // Etiquetas do fluxo (nome -> id novo) para vincular nos cards de exemplo
      const etiquetaIdPorNome = new Map<string, string>();
      const etiquetas = (templateData.etiquetas || []).map((et: any) => {
        const id = genId();
        etiquetaIdPorNome.set(String(et.nome), id);
        return { id, nome: et.nome, cor: et.cor };
      });

      // Montar etapas com novos IDs, preservando SLA / layout / transições
      const etapas = (templateData.etapas || []).map((etapa: any, idx: number) => {
        const oldId = String(etapa.id);
        const newId = etapaIdMap.get(oldId)!;
        const campoMap = campoIdMapByEtapa.get(oldId)!;
        const nova: any = {
          id: newId,
          nome: etapa.nome || '',
          campos: (etapa.campos || []).map((c: any) => {
            const campo: any = {
              id: campoMap.get(String(c.id)) || genId(),
              nome: c.nome || '',
              tipo: c.tipo || 'texto',
              obrigatorio: c.obrigatorio || false,
              opcoes: c.opcoes,
            };
            if (c.coresOpcoes) campo.coresOpcoes = c.coresOpcoes;
            if (c.usarComoTitulo) campo.usarComoTitulo = true;
            if (c.valorMinimo !== undefined) campo.valorMinimo = c.valorMinimo;
            if (c.valorMaximo !== undefined) campo.valorMaximo = c.valorMaximo;
            if (c.precisao !== undefined) campo.precisao = c.precisao;
            if (c.textoInformativo) campo.textoInformativo = c.textoInformativo;
            if (c.ocultarTitulo) campo.ocultarTitulo = true;
            return campo;
          }),
          participantes: [],
          empresaId,
          ordemFluxo: idx,
          camposVisiveisNoCard: mapCampos(etapa.camposVisiveisNoCard),
          permiteMoverPara: mapEtapas(etapa.permiteMoverPara),
        };
        // SLA — é o que alimenta o Dashboard de tempo por etapa
        if (etapa.tempoMaximoHoras !== undefined) {
          nova.tempoMaximoHoras = etapa.tempoMaximoHoras;
          nova.slaIgnorarFimDeSemana = etapa.slaIgnorarFimDeSemana !== false;
          // Escalonamento por e-mail (mesmo formato que a ConfiguracaoEtapa grava)
          if (etapa.escalonamentoSla && etapa.tempoMaximoHoras > 0) {
            nova.escalonamentoSla = { habilitado: true, destinatariosExtras: [], emailsExtras: [] };
          }
        }
        if (etapa.permiteCriarCards !== undefined) nova.permiteCriarCards = etapa.permiteCriarCards;
        if (etapa.ordenacaoCards) nova.ordenacaoCards = etapa.ordenacaoCards;
        if (etapa.limparCamposAoRetornar?.length) {
          nova.limparCamposAoRetornar = { ativo: true, campos: mapCampos(etapa.limparCamposAoRetornar) };
        }
        return nova;
      });

      // Montar cards de exemplo com novos IDs remapeados
      const cards = (templateData.cards || []).map((card: any) => {
        const oldEtapaId = String(card.etapaId);
        const newEtapaId = etapaIdMap.get(oldEtapaId) || etapas[0]?.id || genId();

        // Valores podem referenciar campos de etapas anteriores — usa o índice global
        const campos: Record<string, any> = {};
        if (card.valores) {
          Object.keys(card.valores).forEach((oldCampoId: string) => {
            const newCampoId = campoIdMapGlobal.get(String(oldCampoId));
            if (newCampoId) {
              campos[newCampoId] = card.valores[oldCampoId];
            }
          });
        }

        const etiquetasIds = (card.etiquetas || [])
          .map((nome: string) => etiquetaIdPorNome.get(String(nome)))
          .filter(Boolean) as string[];

        return {
          id: genId(),
          titulo: card.titulo || 'Cartão de Exemplo',
          etapaId: newEtapaId,
          campos,
          ...(etiquetasIds.length ? { etiquetasIds } : {}),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          empresaId,
        };
      });

      // Prazo do processo (relógio único do card), com as etapas de pausa remapeadas
      const prazoTpl = templateData.prazoProcesso;
      const prazoProcesso = prazoTpl && Number(prazoTpl.horas) > 0
        ? {
            habilitado: true,
            horas: Number(prazoTpl.horas),
            ignorarFimDeSemana: !!prazoTpl.ignorarFimDeSemana,
            etapasPausaIds: mapEtapas(prazoTpl.etapasPausaIds),
          }
        : undefined;

      const novoFluxo = {
        name: templateData.name || 'Novo Fluxo',
        descricao: templateData.descricao || '',
        departamentoId: '',
        responsavelId: '',
        participantes: [],
        etapas,
        cards,
        ...(etiquetas.length ? { etiquetas } : {}),
        ...(prazoProcesso ? { prazoProcesso } : {}),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'ativo' as const,
        empresaId,
        createdBy: session?.usuario?.id,
        criadoPorNome: session?.usuario?.nome,
        criadoPorEmail: session?.usuario?.email,
      };

      const { mongoService } = await import('./utils/mongoService');
      const created = await mongoService.createFlow(novoFluxo);

      // Automações do template — criadas depois do fluxo, já com os IDs reais.
      // Falha aqui não invalida o fluxo: avisamos e seguimos.
      const automacoesTemplate = templateData.automacoes || [];
      if (automacoesTemplate.length > 0) {
        const flowIdCriado = String((created as any).id || (created as any)._id);
        let falhas = 0;
        for (const auto of automacoesTemplate) {
          try {
            const etapaOrigemId = etapaIdMap.get(String(auto.etapaOrigemId));
            const campoId = campoIdMapGlobal.get(String(auto.condicao?.campoId));
            if (!etapaOrigemId || !campoId) { falhas++; continue; }

            const condicaoBase = {
              campoId,
              operador: auto.condicao.operador,
              ...(auto.condicao.valor !== undefined ? { valor: auto.condicao.valor } : {}),
            };

            let acoes: any[] = [];
            if (auto.tipo === 'mover') {
              const destino = etapaIdMap.get(String(auto.etapaDestinoId));
              if (!destino) { falhas++; continue; }
              const destinoSenao = auto.etapaDestinoSenaoId
                ? etapaIdMap.get(String(auto.etapaDestinoSenaoId))
                : undefined;
              acoes = [{
                tipo: 'mover_card',
                configuracao: {
                  etapaDestinoId: destino,
                  regrasMover: [{
                    condicao: condicaoBase,
                    acaoEntao: { tipo: 'mover', etapaDestinoId: destino },
                    ...(destinoSenao
                      ? { acaoSenao: { tipo: 'mover', etapaDestinoId: destinoSenao } }
                      : { acaoSenao: { tipo: 'nao_mover' } }),
                  }],
                },
              }];
            } else if (auto.tipo === 'exibir_ocultar') {
              const afetados = (auto.camposAfetadosIds || [])
                .map((cid: string) => {
                  const novoId = campoIdMapGlobal.get(String(cid));
                  if (!novoId) return null;
                  const etapaDona = etapas.find((e: any) => e.campos.some((c: any) => c.id === novoId));
                  const campo = etapaDona?.campos.find((c: any) => c.id === novoId);
                  return { tipo: 'campo', id: novoId, nome: campo?.nome || '' };
                })
                .filter(Boolean);
              if (afetados.length === 0) { falhas++; continue; }
              acoes = [{
                tipo: 'exibir_ocultar',
                configuracao: {
                  regras: [{
                    condicao: condicaoBase,
                    acao: 'mostrar',
                    elementosAfetados: afetados,
                    temEntao: true,
                    temSenao: true,
                    acaoSenao: 'esconder',
                    elementosAfetadosSenao: afetados,
                  }],
                },
              }];
            }

            if (acoes.length === 0) { falhas++; continue; }

            await mongoService.createAutomacao({
              nome: auto.nome,
              flowId: flowIdCriado,
              etapaOrigemId,
              etapaDestinoId: auto.tipo === 'mover' ? etapaIdMap.get(String(auto.etapaDestinoId)) : '',
              condicoes: [],
              acoes,
              tipo: auto.tipo === 'mover' ? 'mover_card' : 'exibir_ocultar',
              tipoGatilho: 'etapa',
              createdAt: new Date(),
              updatedAt: new Date(),
              status: 'ativo',
              empresaId,
            } as any);
          } catch (errAuto) {
            console.error('Falha ao criar automação do template:', errAuto);
            falhas++;
          }
        }
        if (falhas > 0) {
          console.warn(`[template] ${falhas} de ${automacoesTemplate.length} automações não foram criadas`);
        }
      }

      setFlows([...flows, created]);
      hideLoading();
      // Navegar direto para o fluxo criado
      handleNavigateToFlow(created);
    } catch (e: any) {
      console.error('Erro ao criar fluxo a partir de template:', e);
      alert('Não foi possível criar o fluxo a partir do template. ' + (e?.message || ''));
      hideLoading();
    }
  };

  const handleCloneFlow = async (flowDaLista: Flow) => {
    try {
      showLoading('fluxos', 'Clonando fluxo', 'Preparando cópia com automações...');

      // A lista de fluxos chega sem os cards (excludeCards=true), e o clone copia
      // os cards junto — sem buscar o fluxo completo aqui o clone sairia vazio.
      const flowCompleto = await loadSingleFlow(String(flowDaLista.id), false);
      const flow = flowCompleto || flowDaLista;

      const empresaId = session?.empresa?.id;
      if (!empresaId) {
        alert('Selecione uma empresa antes de clonar um fluxo.');
        hideLoading();
        return;
      }

      // Clonar também é criar fluxo: respeita a config global
      if (!podeCriarFluxos()) {
        hideLoading();
        alert('Seu usuário não tem permissão para criar fluxos.');
        return;
      }

      // Criar um clone profundo do fluxo, gerando novos IDs simples
      const genId = () => String(Date.now()) + Math.random().toString(36).slice(2, 8);

      // 1) Mapear ids antigos de etapa -> novos ids e de campos por etapa
      const etapaIdMap = new Map<string, string>();
      const campoIdMapByEtapa = new Map<string, Map<string, string>>(); // oldEtapaId -> (oldCampoId -> newCampoId)

      (flow.etapas || []).forEach((etapa) => {
        const newEtapaId = genId();
        etapaIdMap.set(String(etapa.id), newEtapaId);
        const campoMap = new Map<string, string>();
        etapa.campos.forEach((campo) => {
          campoMap.set(String(campo.id), genId());
        });
        campoIdMapByEtapa.set(String(etapa.id), campoMap);
      });

      // 2) Clonar etapas aplicando os mapas e ajustando refs
      const etapasClonadas = (flow.etapas || []).map((etapa) => {
        const oldEtapaId = String(etapa.id);
        const newEtapaId = etapaIdMap.get(oldEtapaId)!;
        const campoMap = campoIdMapByEtapa.get(oldEtapaId)!;

        const camposClonados = etapa.campos.map((campo) => ({
          ...campo,
          id: campoMap.get(String(campo.id))!,
          createdBy: campo.createdBy,
          integracaoId: campo.integracaoId,
        }));

        const camposVisiveisNoCard = (etapa.camposVisiveisNoCard || []).map((cid) =>
          campoMap.get(String(cid)) || String(cid)
        );

        const permiteMoverPara = (etapa.permiteMoverPara || []).map((eid) =>
          etapaIdMap.get(String(eid)) || String(eid)
        );

        return {
          ...etapa,
          id: newEtapaId,
          campos: camposClonados,
          camposVisiveisNoCard,
          permiteMoverPara,
          participantes: (etapa.participantes || []).map(String),
          empresaId: empresaId,
        };
      });

      // 3) Clonar cards, remapeando etapaId e chaves dos campos para os novos ids
      const cardsClonados = (flow.cards || []).map((card) => {
        const oldEtapaId = String(card.etapaId);
        const newEtapaId = etapaIdMap.get(oldEtapaId) || genId();
        const campoMap = campoIdMapByEtapa.get(oldEtapaId);

        const novosCampos: Record<string, any> = {};
        const camposOrig = card.campos || {};
        Object.keys(camposOrig).forEach((oldCampoId) => {
          const newCampoId = campoMap?.get(String(oldCampoId));
          if (newCampoId) {
            novosCampos[newCampoId] = camposOrig[oldCampoId];
          }
        });

        return {
          ...card,
          id: genId(),
          etapaId: newEtapaId,
          campos: novosCampos,
          createdAt: new Date(),
          updatedAt: new Date(),
          empresaId: empresaId,
          etapaHistory: undefined,
          lastEditedBy: undefined,
          lastEditedAt: undefined,
          fieldHistory: undefined,
        };
      });

      const novoFluxo = {
        name: `${flow.name} (Cópia)`,
        descricao: flow.descricao,
        departamentoId: String(flow.departamentoId || ''),
        responsavelId: String(flow.responsavelId || ''),
        participantes: (flow.participantes || []).map(String),
        etapas: etapasClonadas,
        cards: cardsClonados,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'ativo' as const,
        empresaId: empresaId,
        createdBy: session?.usuario?.id,
        criadoPorNome: session?.usuario?.nome,
        criadoPorEmail: session?.usuario?.email,
      };

      const { mongoService } = await import('./utils/mongoService');
      const created = await mongoService.createFlow(novoFluxo);

      // 4) Preparar clonagem de automações: levantar templates usados e criar clones
      updateMessage('Clonando automações', 'Remapeando campos, etapas e templates...');
      const automacoesDoFlow = automacoes.filter(a => a.flowId === flow.id);

      // Coletar todos os templateIds referenciados nas automações
      const templateIdsUsados = new Set<number | string>();
      const coletarTemplates = (auto: any) => {
        const pushId = (id: any) => { if (id !== undefined && id !== null && id !== '') templateIdsUsados.add(id); };
        if (auto?.acoes) {
          auto.acoes.forEach((acao: any) => {
            const conf = acao?.configuracao || {};
            if (acao?.tipo === 'enviar_email') {
              if (conf.templateId) pushId(conf.templateId);
              if (Array.isArray(conf.regrasEmail)) {
                conf.regrasEmail.forEach((r: any) => {
                  if (r?.acaoEntao?.templateId) pushId(r.acaoEntao.templateId);
                  if (r?.acaoSenao?.templateId) pushId(r.acaoSenao.templateId);
                });
              }
            }
          });
        }
      };
      automacoesDoFlow.forEach(coletarTemplates);

      // Buscar templates e criar clones quando necessário
      const templateMap = new Map<any, any>(); // oldId -> newId
      if (templateIdsUsados.size > 0) {
        try {
          const respTemplates = await fetch('/api/templates-email');
          if (respTemplates.ok) {
            const data = await respTemplates.json();
            const todosTemplates = data?.templates || [];
            for (const oldId of templateIdsUsados) {
              const tpl = todosTemplates.find((t: any) => String(t.id) === String(oldId));
              if (!tpl) continue;
              const clonePayload = {
                nome: `${tpl.nome} (Cópia)`,
                assunto: tpl.assunto,
                conteudo: tpl.conteudo,
                destinatarios: tpl.destinatarios || [],
                cc: tpl.cc || [],
                bcc: tpl.bcc || [],
                campos_utilizados: tpl.campos_utilizados || [],
                ativo: tpl.ativo !== false
              };
              const createResp = await fetch('/api/templates-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(clonePayload)
              });
              if (createResp.ok) {
                const createdTpl = await createResp.json();
                const newId = createdTpl?.template?.id ?? createdTpl?.data?.id;
                if (newId !== undefined) templateMap.set(oldId, newId);
              }
            }
          }
        } catch (err) {
          console.warn('Falha ao clonar templates de e-mail:', err);
        }
      }

      const createdAutomacoes: Automacao[] = [];
      for (const auto of automacoesDoFlow) {
        const oldOrigemId = String(auto.etapaOrigemId);
        const campoMapOrigem = campoIdMapByEtapa.get(oldOrigemId);

        const mapCampo = (campoId?: string) =>
          campoId ? (campoMapOrigem?.get(String(campoId)) || String(campoId)) : campoId;
        const mapEtapa = (etapaId?: string) =>
          etapaId ? (etapaIdMap.get(String(etapaId)) || String(etapaId)) : etapaId;

        const cloneDeep = (obj: any) => JSON.parse(JSON.stringify(obj));

        const autoClonadaBase: any = cloneDeep(auto);
        delete autoClonadaBase._id;
        delete autoClonadaBase.id;
        autoClonadaBase.nome = `${auto.nome || 'Automação'} (Cópia)`;
        autoClonadaBase.flowId = created.id;
        autoClonadaBase.etapaOrigemId = mapEtapa(auto.etapaOrigemId);
        autoClonadaBase.etapaDestinoId = mapEtapa(auto.etapaDestinoId);
        autoClonadaBase.empresaId = empresaId;
        autoClonadaBase.createdAt = new Date().toISOString();
        autoClonadaBase.updatedAt = new Date().toISOString();

        // Remap condicoes (campoId)
        if (Array.isArray(autoClonadaBase.condicoes)) {
          autoClonadaBase.condicoes = autoClonadaBase.condicoes.map((c: any) => ({
            ...c,
            campoId: mapCampo(c.campoId)
          }));
        }

  // Remap acoes (inclui templateId quando enviar_email)
        if (Array.isArray(autoClonadaBase.acoes)) {
          autoClonadaBase.acoes = autoClonadaBase.acoes.map((acao: any) => {
            if (!acao?.configuracao) return acao;
            const conf = { ...acao.configuracao };

            if (acao.tipo === 'mover_card') {
              if (conf.etapaDestinoId) conf.etapaDestinoId = mapEtapa(conf.etapaDestinoId);
              if (Array.isArray(conf.regrasMover)) {
                conf.regrasMover = conf.regrasMover.map((regra: any) => ({
                  ...regra,
                  condicao: regra?.condicao ? { ...regra.condicao, campoId: mapCampo(regra.condicao.campoId) } : regra?.condicao,
                  acaoEntao: regra?.acaoEntao ? { ...regra.acaoEntao, etapaDestinoId: mapEtapa(regra.acaoEntao.etapaDestinoId) } : regra?.acaoEntao,
                  acaoSenao: regra?.acaoSenao ? { ...regra.acaoSenao, etapaDestinoId: mapEtapa(regra.acaoSenao.etapaDestinoId) } : regra?.acaoSenao,
                }));
              }
            }

            if (acao.tipo === 'enviar_email') {
              if (conf.templateId && templateMap.has(conf.templateId)) {
                conf.templateId = templateMap.get(conf.templateId);
              }
            }
            if (Array.isArray(conf.regrasEmail)) {
              conf.regrasEmail = conf.regrasEmail.map((regra: any) => ({
                ...regra,
                condicao: regra?.condicao ? { ...regra.condicao, campoId: mapCampo(regra.condicao.campoId) } : regra?.condicao,
                acaoEntao: regra?.acaoEntao ? {
                  ...regra.acaoEntao,
                  templateId: (regra.acaoEntao.templateId && templateMap.has(regra.acaoEntao.templateId)) ? templateMap.get(regra.acaoEntao.templateId) : regra.acaoEntao.templateId
                } : regra?.acaoEntao,
                acaoSenao: regra?.acaoSenao ? {
                  ...regra.acaoSenao,
                  templateId: (regra.acaoSenao.templateId && templateMap.has(regra.acaoSenao.templateId)) ? templateMap.get(regra.acaoSenao.templateId) : regra.acaoSenao.templateId
                } : regra?.acaoSenao,
              }));
            }

            if (Array.isArray(conf.regras)) {
              conf.regras = conf.regras.map((regra: any) => ({
                ...regra,
                condicao: regra?.condicao ? { ...regra.condicao, campoId: mapCampo(regra.condicao.campoId) } : regra?.condicao,
                elementosAfetados: Array.isArray(regra.elementosAfetados)
                  ? regra.elementosAfetados.map((el: any) => ({
                      ...el,
                      id: el?.tipo === 'campo' ? mapCampo(el.id) : el.id,
                    }))
                  : regra.elementosAfetados,
                elementosAfetadosSenao: Array.isArray(regra.elementosAfetadosSenao)
                  ? regra.elementosAfetadosSenao.map((el: any) => ({
                      ...el,
                      id: el?.tipo === 'campo' ? mapCampo(el.id) : el.id,
                    }))
                  : regra.elementosAfetadosSenao,
              }));
            }

            return { ...acao, configuracao: conf };
          });
        }

        // Persistir automação clonada
        const userHeader = {
          email: (session as any)?.usuario?.email || (session as any)?.user?.email || (session as any)?.email || 'admin@local',
          role: (session as any)?.usuario?.role || (session as any)?.user?.role || (session as any)?.role || 'admin',
          empresaId: (session as any)?.empresa?.id
        };
        const resp = await fetch('/api/automacoes', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-User-Data': JSON.stringify(userHeader)
          },
          body: JSON.stringify(autoClonadaBase),
        });
        if (resp.ok) {
          const data = await resp.json();
          const autoCriada = data?.data || data; // API pode retornar direto ou embrulhado
          if (autoCriada && (autoCriada._id || autoCriada.id)) {
            const id = autoCriada.id || autoCriada._id;
            createdAutomacoes.push({ ...(autoClonadaBase as any), id } as Automacao);
          }
        } else {
          console.warn('Falha ao clonar automação:', auto.nome);
        }
      }

      // 5) Clonar conexões entre fluxos que referenciam este fluxo
      updateMessage('Clonando conexões', 'Atualizando referências entre fluxos...');
      try {
        const conexoes = await mongoService.getConexoesFluxos();
        const relacionadas = (conexoes || []).filter((cx: any) => {
          const origem = String(cx.fluxoOrigemId) === String(flow.id);
          const destino = Array.isArray(cx.acoes) && cx.acoes.some((a: any) => String(a.fluxoDestinoId) === String(flow.id));
          return origem || destino;
        });

        for (const cx of relacionadas) {
          const nova = JSON.parse(JSON.stringify(cx));
          delete nova._id; delete nova.id;
          nova.nome = `${cx.nome || 'Conexão'} (Cópia)`;
          nova.createdAt = new Date().toISOString();
          nova.updatedAt = new Date().toISOString();

          // Remap origem se necessário
          const origemEraEsteFluxo = String(cx.fluxoOrigemId) === String(flow.id);
          if (origemEraEsteFluxo) {
            nova.fluxoOrigemId = created.id;
            nova.etapaOrigemId = etapaIdMap.get(String(cx.etapaOrigemId)) || nova.etapaOrigemId;
            if (Array.isArray(nova.condicoes)) {
              const campoMapOrigem = campoIdMapByEtapa.get(String(cx.etapaOrigemId));
              nova.condicoes = nova.condicoes.map((c: any) => ({
                ...c,
                campoId: c?.campoId ? (campoMapOrigem?.get(String(c.campoId)) || c.campoId) : c?.campoId
              }));
            }
          }

          // Remap destinos nas ações
          if (Array.isArray(nova.acoes)) {
            nova.acoes = nova.acoes.map((a: any) => {
              const out = { ...a };
              const destinoEraEsteFluxo = String(a.fluxoDestinoId) === String(flow.id);
              if (destinoEraEsteFluxo) {
                out.fluxoDestinoId = created.id;
                if (out.etapaDestinoId) {
                  const oldEtapaDest = String(a.etapaDestinoId);
                  out.etapaDestinoId = etapaIdMap.get(oldEtapaDest) || out.etapaDestinoId;
                  // Remap chaves de campos se existir payload campos
                  if (out.campos && typeof out.campos === 'object') {
                    const campoMapDest = campoIdMapByEtapa.get(oldEtapaDest);
                    const novosCampos: Record<string, any> = {};
                    Object.keys(out.campos).forEach((oldCampoId: string) => {
                      const newCampoId = campoMapDest?.get(String(oldCampoId));
                      if (newCampoId) novosCampos[newCampoId] = out.campos[oldCampoId];
                    });
                    out.campos = novosCampos;
                  }
                }
              }
              return out;
            });
          }

          try {
            await mongoService.createConexaoFluxo(nova);
          } catch (err) {
            console.warn('Falha ao clonar conexão:', cx?.nome, err);
          }
        }
      } catch (err) {
        console.warn('Erro ao buscar/clonar conexões do fluxo:', err);
      }

      // Atualizar estados locais
      setFlows([...flows, created]);
      if (createdAutomacoes.length) {
        setAutomacoes([...automacoes, ...createdAutomacoes]);
      }
      // Toast de sucesso com link para abrir o novo fluxo
      toast.success('Fluxo, automações e conexões clonados com sucesso.', {
        actionLabel: 'Abrir fluxo copiado',
        onAction: () => handleNavigateToFlow(created),
        durationMs: 8000
      });

      hideLoading();
    } catch (e) {
      console.error('Erro ao clonar fluxo:', e);
      alert('Não foi possível clonar o fluxo.');
      hideLoading();
    }
  };

  const handleNavigateToFlow = async (flow: Flow, cardIdToOpen?: string) => {
    // 🔄 Carregar apenas ESTE flow com seus cards (rápido!)
    console.log('📥 Carregando flow específico:', flow.id);
    let flowComCards = flow;
    
    try {
      const response = await fetch(`/api/flows/${flow.id}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          flowComCards = result.data;
          console.log('✅ Flow carregado com', flowComCards.cards?.length || 0, 'cards');
        } else {
          console.warn('⚠️ Resposta sem dados:', result);
        }
      } else {
        console.error('❌ Erro HTTP:', response.status);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar flow:', error);
    }
    
    const normalizedFlow = {
      ...flowComCards,
      id: String(flowComCards.id || flow.id),
      departamentoId: String(flowComCards.departamentoId || flow.departamentoId),
      responsavelId: String(flowComCards.responsavelId || flow.responsavelId),
      participantes: (flowComCards.participantes || flow.participantes || []).map(String),
      empresaId: session?.empresa?.id || '',
      etapas: (flowComCards.etapas || flow.etapas)?.map(etapa => ({
        ...etapa,
        id: String(etapa.id),
        departamentoId: String(etapa.departamentoId),
        participantes: (etapa.participantes ?? []).map(String),
        campos: etapa.campos.map(campo => ({
          ...campo,
          id: String(campo.id)
        }))
      })) || [],
      cards: (flowComCards.cards || flow.cards)?.map(card => ({
        ...card,
        id: String(card.id),
        etapaId: String(card.etapaId),
        campos: card.campos || {}
      })) || []
    };
    
    console.log('🔍 Flow normalizado com campos:', normalizedFlow);
    console.log('🔍 Cards do flow:', normalizedFlow.cards);
    normalizedFlow.cards?.forEach((card, index) => {
      console.log(`🔍 Card ${index + 1} - ${card.titulo}:`, card.campos);
    });
    
    setSelectedFlow(normalizedFlow);
    setCurrentSection('flows');
    
    // Se foi solicitado abrir um card específico (ex: card conectado com obrigar preenchimento)
    if (cardIdToOpen) {
      setCardToOpen(cardIdToOpen);
    }
  };

  const handleEditColaborador = async (updatedColaborador: Omit<Colaborador, 'id' | 'createdAt' | 'updatedAt'> | Colaborador) => {
    const empresaId = session?.empresa?.id;
    if (!empresaId) return;

    let newColaboradores;
    if ('id' in updatedColaborador) {
      const normalizedColaborador = {
        ...updatedColaborador,
        id: String(updatedColaborador.id),
        departamentoId: String(updatedColaborador.departamentoId),
        empresaId: empresaId,
        updatedAt: new Date()
      };
      
      // ✅ CORREÇÃO: Salvar no MongoDB primeiro
      try {
        const { mongoService } = await import('./utils/mongoService');
        await mongoService.updateColaborador(normalizedColaborador.id, normalizedColaborador);
        console.log('✅ Colaborador atualizado no MongoDB:', normalizedColaborador.nome);
      } catch (error) {
        console.error('❌ Erro ao salvar colaborador no MongoDB:', error);
        alert('Erro ao salvar colaborador. Tente novamente.');
        return;
      }
      
      newColaboradores = colaboradores.map(col => 
        col.id === normalizedColaborador.id ? normalizedColaborador : col
      );
    } else {
      const newColaborador: Colaborador = {
        id: String(Date.now()),
        ...updatedColaborador,
        departamentoId: String(updatedColaborador.departamentoId),
        empresaId: empresaId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // ✅ CORREÇÃO: Criar no MongoDB
      try {
        const { mongoService } = await import('./utils/mongoService');
        await mongoService.createColaborador(newColaborador);
        console.log('✅ Colaborador criado no MongoDB:', newColaborador.nome);
      } catch (error) {
        console.error('❌ Erro ao criar colaborador no MongoDB:', error);
        alert('Erro ao criar colaborador. Tente novamente.');
        return;
      }
      
      newColaboradores = [...colaboradores, newColaborador];
    }

    setColaboradores(newColaboradores);
    saveData('colaboradores', newColaboradores);
    setViewingColaborador(null);
    setEditingColaborador(null);
  };

  const handleSelectEmpresa = (user: User, empresaId?: string) => {
    try {
      if (empresaId) {
        const empresa = empresas.find(e => e.id === empresaId);
        if (!empresa) {
          throw new Error('Empresa não encontrada');
        }
        
        // Criar sessão com empresa selecionada
        const userSession: UserSession = {
          usuario: user,
          empresa
        };
        
        setSession(userSession);
        localStorage.setItem('session', JSON.stringify(userSession));
      } else {
        // Login sem empresa específica (admin)
        const userSession: UserSession = {
          usuario: user
        };
        
        setSession(userSession);
        localStorage.setItem('session', JSON.stringify(userSession));
      }
      
      navigate('/');
    } catch (error) {
      console.error('Erro na seleção da empresa:', error);
      throw error;
    }
  };

  // ── Onboarding: mostrar tour no primeiro acesso ──
  useEffect(() => {
    if (!session?.usuario) return;
    // Só mostrar se dados já carregaram (não durante loading)
    if (loading.isLoading) return;
    // Não mostrar tour se o trial expirou (tela de planos aparece em cima)
    if (planoInfo?.trial_expirado) {
      setShowOnboarding(false);
      return;
    }
    
    const tourDismissedKey = `onboarding_dismissed_${session.usuario.id || session.usuario.email}`;
    const alreadyDismissed = localStorage.getItem(tourDismissedKey);

    // Se todos os 3 requisitos já foram concluídos, dispensar o tour automaticamente
    const allRequirementsDone = departamentos.length > 0 && colaboradores.length > 0 && flows.length > 0;
    if (allRequirementsDone) {
      if (!alreadyDismissed) {
        localStorage.setItem(tourDismissedKey, 'true');
      }
      setShowOnboarding(false);
      return;
    }

    if (alreadyDismissed) return;
    
    // Primeiro acesso: sem fluxos E sem departamentos
    if (flows.length === 0 && departamentos.length === 0) {
      setShowOnboarding(true);
    }
  }, [session?.usuario?.id, flows.length, departamentos.length, colaboradores.length, loading.isLoading, planoInfo?.trial_expirado]);

  // Função para verificar permissões do usuário
  const checkUserPermission = (requiredPermission: 'view' | 'edit' | 'admin', targetData?: any): boolean => {
    if (!session?.usuario) return false;
    
    const userRole = session.usuario.role;
    const currentUserId = String(session.usuario.id);
    
    // Administrador tem acesso total (administrator, admin, company_admin, global_admin)
    if (['administrator', 'admin', 'company_admin', 'global_admin'].includes(userRole)) return true;
    
    // Usuário de consulta só pode visualizar
    if (userRole === 'viewer') {
      return requiredPermission === 'view';
    }
    
    // Editor pode visualizar tudo da empresa, mas editar com restrições
    if (userRole === 'editor') {
      // Editores podem visualizar todos os fluxos e dados da empresa
      if (requiredPermission === 'view') return true;
      
      if (requiredPermission === 'edit') {
        // ✅ EDITORES PODEM CRIAR NOVOS FLUXOS (sem targetData significa criação)
        if (!targetData) {
          console.log('✅ Editor pode CRIAR novos fluxos/dados');
          return true;
        }
        
        // Para fluxos específicos, verificar se é criador
        if (targetData?.type === 'flow') {
          const flow = targetData.data;
          
          // ✅ EDITORES SÓ PODEM EDITAR FLUXO COMPLETO SE FOREM CRIADORES
          const isCreator = flow.createdBy === currentUserId;
          
          console.log('🔐 Verificação de permissões de edição de FLUXO:', {
            fluxo: flow.name,
            isCreator,
            currentUserId,
            flowCreatedBy: flow.createdBy,
            canEditFlow: isCreator
          });
          
          // Editor só pode editar o FLUXO COMPLETO se criou
          // Para editar ETAPAS específicas, usa checkCardEditPermission
          return isCreator;
        }
        // Para outros dados, pode editar se estiver no mesmo departamento
        if (targetData?.departamentoId) {
          return targetData.departamentoId === session.usuario.departamentoId;
        }
        // Sem targetData específico, editor pode criar
        return true;
      }
      return false;
    }
    
    return false;
  };

  // CRM fica liberado pra: admins + departamento "Comercial" + flag manual `crmAcesso=true` + permissão de menu explícita.
  const checkCRMAccess = (): boolean => {
    if (checkUserPermission('admin')) return true;
    const u: any = session?.usuario;
    if (!u) return false;
    // Flag manual no doc do usuario (admin libera caso a caso)
    if (u.crmAcesso === true) return true;
    // Permissão granular de menu
    const menuPerms: any[] = u.permissoes?.menus || [];
    if (menuPerms.some((p: any) => p.modulo === 'crm')) return true;
    // String direto na sessao (alguns docs guardam o nome)
    const deptName = (u.departamento || u.departamentoNome || '').toString().toLowerCase();
    if (deptName.includes('comercial')) return true;
    // Via id -> lookup na lista carregada de departamentos
    const deptId = u.departamentoId;
    if (!deptId) return false;
    const dept = departamentos.find(d => String(d.id) === String(deptId));
    return !!dept && (dept.nome || '').toLowerCase().includes('comercial');
  };

  // Verifica permissão de acesso a um módulo de menu específico.
  // Admins têm acesso total; não-admins precisam de entrada explícita em permissoes.menus.
  // fluxoId opcional: se fornecido, verifica se o escopo do módulo inclui esse fluxo.
  const checkMenuAccess = (modulo: string, fluxoId?: string): { canView: boolean; canEdit: boolean } => {
    if (checkUserPermission('admin')) return { canView: true, canEdit: true };
    const u: any = session?.usuario;
    if (!u) return { canView: false, canEdit: false };
    const menuPerms: any[] = u.permissoes?.menus || [];
    const perm = menuPerms.find((p: any) => p.modulo === modulo);
    if (!perm) return { canView: false, canEdit: false };
    // Verificar escopo por fluxo (se fluxosIds vazio = todos os fluxos)
    if (fluxoId && perm.fluxosIds && perm.fluxosIds.length > 0) {
      if (!perm.fluxosIds.includes(fluxoId)) return { canView: false, canEdit: false };
    }
    return { canView: true, canEdit: perm.nivel === 'editar' };
  };

  // Função para verificar se usuário pode editar etapas (criar/editar/deletar etapas)
  const checkEtapaEditPermission = (): boolean => {
    if (!session?.usuario) return false;
    
    const userRole = session.usuario.role;
    const userId = String(session.usuario.id);
    
    // Administrador tem acesso total
    if (userRole === 'administrator') return true;

    // 🔑 Permissão granular concedida via "Configuração de Permissões" do fluxo
    // (toggle "Configurar etapas" em ModalConfigPermissoes)
    try {
      const userEmail = session.usuario.email?.toLowerCase();
      const colaboradorDoUsuario = colaboradores.find(col =>
        (col.email && userEmail && col.email.toLowerCase() === userEmail) ||
        String(col.id) === userId
      );
      const possiveisIds = [
        userId,
        colaboradorDoUsuario ? String(colaboradorDoUsuario.id) : null,
        colaboradorDoUsuario ? String((colaboradorDoUsuario as any)._id) : null,
      ].filter(Boolean);
      const permGranular = flowPermissoes.find((p: any) =>
        possiveisIds.includes(String(p.colaboradorId))
      );
      if (permGranular?.permissoesGerais?.configurarEtapas === true ||
          permGranular?.permissoesGerais?.configurarEtapas?.permitido === true) {
        return true;
      }
    } catch { /* ignora */ }
    
    // Editor pode editar etapas APENAS em fluxos que ele criou ou onde é participante
    if (userRole === 'editor') {
      const isFlowCreator = selectedFlow?.createdBy === userId;
      
      // Verificar se é participante de alguma etapa
      const isParticipant = selectedFlow?.etapas?.some((etapa: any) => 
        etapa.participantes?.includes(userId) ||
        etapa.departamentoId === session.usuario.departamentoId
      );
      
      console.log('🔐 Verificação de permissões de edição de etapa:', {
        fluxo: selectedFlow?.name,
        isFlowCreator,
        isParticipant,
        userId,
        flowCreatedBy: selectedFlow?.createdBy,
        canEditEtapa: isFlowCreator || isParticipant
      });
      
      return Boolean(isFlowCreator || isParticipant);
    }
    
    // Viewers não podem editar etapas
    return false;
  };

  // Função para verificar se usuário pode editar cartão baseado na etapa
  const checkCardEditPermission = (etapa: any, card?: any): boolean => {
    if (!session?.usuario) return false;
    
    const userRole = session.usuario.role;
    const userId = String(session.usuario.id);
    
    // ✈️ Etapas de aprovação do Viagens (Dayane, 29/10/2026): só quem é aprovador decide —
    // administrador comum também não passa; o admin master fica de suporte.
    //   aprovadorPorCard: líder calculado pelo servidor e gravado em card.liderancaAprovadores
    //   aprovadoresEtapa: lista fixa da etapa { ids, emails } (Aprovação Gestão = só o Samuel Branco)
    if (etapa?.aprovadorPorCard || etapa?.aprovadoresEtapa) {
      if (ehAdminMaster(session.usuario.email)) return true;
      // superAprovadores: e-mails liberados em todas as aprovações da etapa (Daiane/controladoria, 29/10)
      if ((etapa.superAprovadores || []).map((x: any) => String(x).toLowerCase()).includes(String(session.usuario.email || '').toLowerCase())) return true;
      const apr = etapa.aprovadorPorCard ? (card?.liderancaAprovadores || {}) : (etapa.aprovadoresEtapa || {});
      const lista = [...(apr.ids || []), ...(apr.emails || [])].map((x: any) => String(x).toLowerCase());
      const email = session.usuario.email?.toLowerCase();
      const meus = colaboradores.filter(col =>
        (col.userId && String(col.userId) === userId) ||
        (col.email && email && col.email.toLowerCase() === email));
      const candidatos = [userId, email || '', ...meus.flatMap(c => [String(c.id), String((c as any)._id || '')])]
        .filter(Boolean).map(x => String(x).toLowerCase());
      return candidatos.some(c => lista.includes(c));
    }

    // Administrador tem acesso total (ignorando restrições da etapa).
    // 'admin' = cadastros antigos, mesmo critério do isAdminUser.
    if (userRole === 'administrator' || (userRole as string) === 'admin') {
      return true;
    }

    // 🔒 Etapa com soDonoEdita (Compras Revenda, 14/10/2026): além de participar da etapa,
    // a pessoa só mexe no card que é dela (responsável ou quem criou) ou de alguém da equipe
    // dela (o chefe direto do responsável). O comprador vê a demanda do outro, mas não mexe.
    if (etapa?.soDonoEdita && card && (userRole === 'editor' || userRole === 'viewer')) {
      const email = session.usuario.email?.toLowerCase();
      const meus = colaboradores.filter(col =>
        (col.userId && String(col.userId) === userId) ||
        (col.email && email && col.email.toLowerCase() === email));
      const meusIds = new Set([userId, ...meus.flatMap(c => [String(c.id), String((c as any)._id || '')])].filter(Boolean));
      const donoId = String(card.responsavelIndividualId || '');
      const dono = colaboradores.find(c => String(c.id) === donoId || String((c as any)._id) === donoId);
      const ehDono = (!!donoId && meusIds.has(donoId)) || (!!card.criadoPor && meusIds.has(String(card.criadoPor)));
      const ehChefe = !!dono && meusIds.has(String((dono as any).superiorId || ''));
      if (!ehDono && !ehChefe) return false;
    }
    
    // ⚠️ IMPORTANTE: permiteCriarCards controla apenas a CRIAÇÃO de novos cards
    // NÃO deve bloquear a EDIÇÃO de cards existentes!
    // Editores podem editar cards se forem participantes, independente de permiteCriarCards
    
    // Editor e usuário de consulta podem editar/criar SE forem participantes da etapa.
    // A lista de participantes guarda ids de COLABORADOR e o login é um USER (cadastros
    // separados), então a pessoa é reconhecida por: id do login, colaborador vinculado ao
    // login (userId) ou colaborador com o mesmo e-mail. O viewer comparava só o id do
    // login e nunca batia; o editor dependia só do e-mail ser igual nos dois cadastros.
    // abertaParaTodos (Televendas ↔ Logística, 29/10/2026): qualquer pessoa da empresa abre e mexe no
    // card nesta etapa — junto com soDonoEdita, cada um só mexe no que é seu.
    if ((userRole === 'editor' || userRole === 'viewer') && etapa?.abertaParaTodos) return true;

    if (userRole === 'editor' || userRole === 'viewer') {
      const participantes = (etapa.participantes || []).map((p: any) => String(p));
      const userEmail = session.usuario.email?.toLowerCase();

      const colaboradoresDoUsuario = colaboradores.filter(col =>
        (col.userId && String(col.userId) === userId) ||
        (col.email && userEmail && col.email.toLowerCase() === userEmail)
      );

      const isParticipant =
        participantes.includes(userId) ||
        colaboradoresDoUsuario.some(col =>
          participantes.includes(String(col.id)) || participantes.includes(String((col as any)._id))
        ) ||
        (!!userEmail && participantes.some((p: string) => p.toLowerCase() === userEmail));

      if (!isParticipant) {
        console.log(`🔐 [${etapa.nome}] ${userRole} não é participante:`,
          { userId, userEmail, colaboradores: colaboradoresDoUsuario.map(c => `${c.nome} (${c.id})`), participantes });
      }
      return isParticipant;
    }

    return false;
  };

  // Função para calcular cards vencidos por colaborador
  const calcularCardsVencidos = (): { [colaboradorId: string]: number } => {
    const cardsVencidosPorColaborador: { [colaboradorId: string]: number } = {};
    
    flowsComCardsResumo.forEach(flow => {
      flow.cards?.forEach(card => {
        // Encontrar a etapa do card
        const etapa = flow.etapas?.find(e => String(e.id) === String(card.etapaId));
        if (!etapa) return;
        
        // Procurar campo de data_vencimento nos campos da etapa
        const campoVencimento = etapa.campos.find(campo => campo.tipo === 'data_vencimento');
        if (!campoVencimento) return;
        
        const dataVencimento = card.campos?.[campoVencimento.id];
        if (!dataVencimento) return;
        
        try {
          const vencimento = new Date(dataVencimento);
          const agora = new Date();
          
          // Se já venceu
          if (vencimento < agora) {
            // PRIORIDADE 1: Se o card tem um responsável individual, atribuir apenas a ele
            if (card.responsavelIndividualId) {
              const colaboradorId = String(card.responsavelIndividualId);
              if (!cardsVencidosPorColaborador[colaboradorId]) {
                cardsVencidosPorColaborador[colaboradorId] = 0;
              }
              cardsVencidosPorColaborador[colaboradorId]++;
            } else {
              // PRIORIDADE 2: Se não tem responsável individual, contar para todos os participantes da etapa
              etapa.participantes?.forEach(participanteId => {
                const colaboradorId = String(participanteId);
                if (!cardsVencidosPorColaborador[colaboradorId]) {
                  cardsVencidosPorColaborador[colaboradorId] = 0;
                }
                cardsVencidosPorColaborador[colaboradorId]++;
              });
            }
          }
        } catch (error) {
          console.warn('Erro ao processar data de vencimento:', error);
        }
      });
    });
    
    return cardsVencidosPorColaborador;
  };

  const renderMainContent = () => {
    switch (currentSection) {
      case 'home':
        // Mostrar loading enquanto os dados estão sendo carregados
        if (loading.isLoading && (loading.type === 'mongodb' || loading.type === 'fluxos')) {
          return <EscaloneLoading />;
        }
        
        return (
          <div className="space-y-6">
            <HomeComponent
              flows={flowsComCardsResumo}
              session={session || undefined}
              colaboradores={colaboradores}
              onCardClick={(flowId, cardId) => {
                // Navegar para o flow
                const flow = flows.find(f => String(f.id) === String(flowId));
                if (flow) {
                  handleNavigateToFlow(flow);
                  // Guardar o card a ser aberto
                  setCardToOpen(cardId);
                }
              }}
              onNavigate={(path) => {
                if (path === '/dashboards') {
                  setCurrentSection('dashboards');
                } else if (path === '/fluxos') {
                  setCurrentSection('kanban');
                }
              }}
            />
          </div>
        );

      case 'tasks':
        return (
          <TasksComponent
            tasks={tasks}
            colaboradores={colaboradores}
            departamentos={departamentos}
            session={session!}
            onCreateTask={handleCreateTask}
            onUpdateTask={handleUpdateTask}
            onDeleteTask={handleDeleteTask}
            initialTaskId={pendingTaskId}
            onTaskOpened={handleTaskOpened}
          />
        );

      case 'dashboards':
        return (
          <Dashboard
            flows={flows}
            cards={cardsResumo}
            departamentos={departamentos}
            colaboradores={colaboradores}
          />
        );

      case 'empresas':
        return (
          <ErrorBoundary>
            <Empresas
              initialEmpresas={empresas}
              onEmpresasChange={handleEmpresasChange}
              planoInfo={planoInfo}
              onNavigate={(section) => setCurrentSection(section as any)}
              onSelectEmpresa={(empresa) => {
                try {
                  if (!session) return;
                  
                  showLoading('empresas', 'Selecionando empresa', 'Carregando dados da empresa...');
                  
                  const updatedSession = {
                    ...session,
                    empresa,
                    usuario: {
                      ...session.usuario,
                      empresaId: empresa.id
                    }
                  };
                  
                  setSession(updatedSession);
                  localStorage.setItem('session', JSON.stringify(updatedSession));
                  
                  // Carregar dados da nova empresa
                  setTimeout(() => {
                    setCurrentSection('home');
                    hideLoading();
                  }, 500);
                  
                } catch (error) {
                  console.error('❌ Erro ao selecionar empresa:', error);
                  hideLoading();
                }
              }}
              session={session || undefined}
            />
          </ErrorBoundary>
        );

      case 'flows':
        // Se estiver carregando dados do MongoDB, mostrar loading
        if (loading.isLoading && (loading.type === 'mongodb' || loading.type === 'fluxos')) {
          return <EscaloneLoading />;
        }
        
        return selectedFlow ? (
          <KanbanBoard
            flow={selectedFlow}
            departamentos={departamentos}
            colaboradores={colaboradores}
            onUpdateFlow={handleUpdateFlow}
            onBack={() => setSelectedFlow(null)}
            onNavigateToFlow={handleNavigateToFlow}
            onOpenEtapaConfig={collapseMenuIfDesktop}
            onCloseEtapaConfig={restoreMenuIfDesktop}
            userPermissions={{
              canEdit: checkUserPermission('edit'),
              canAdmin: checkUserPermission('admin'),
              currentUser: session?.usuario,
              configurarEtapas: (() => {
                try {
                  const userId = String(session?.usuario?.id || '');
                  const userEmail = session?.usuario?.email?.toLowerCase();
                  const colab = colaboradores.find((col: any) =>
                    (col.email && userEmail && col.email.toLowerCase() === userEmail) ||
                    String(col.id) === userId
                  );
                  const possiveisIds = [
                    userId,
                    colab ? String(colab.id) : null,
                    colab ? String((colab as any)._id) : null,
                  ].filter(Boolean);
                  const perm = flowPermissoes.find((p: any) => possiveisIds.includes(String(p.colaboradorId)));
                  const cfg = perm?.permissoesGerais?.configurarEtapas;
                  if (!cfg) return null;
                  return typeof cfg === 'boolean' ? { permitido: cfg, etapas: [], secoes: [] } : cfg;
                } catch { return null; }
              })()
            }}
            checkCardEditPermission={checkCardEditPermission}
            checkEtapaEditPermission={checkEtapaEditPermission}
            automacoes={automacoes.filter(a => a.flowId === selectedFlow.id)}
            cardToOpen={cardToOpen}
            onCardOpened={() => setCardToOpen(null)}
            onUpdateCache={(cards) => updateFlowCacheWithCards(selectedFlow.id, cards)}
          />
        ) : (
          <FlowList
            flows={(() => {
              // Aplicar config de visibilidade de fluxos para o usuário atual
              const isAdmin = checkUserPermission('admin');
              if (isAdmin) return flows; // Admin vê tudo
              const userId = String(session?.usuario?.id || '');
              const colaboradorId = colaboradores.find(c =>
                String(c.id || (c as any)._id) === userId ||
                c.email === session?.usuario?.email
              );
              const colabId = colaboradorId ? String(colaboradorId.id || (colaboradorId as any)._id) : userId;
              const conf = flowGlobalConfig.find(c => c.colaboradorId === colabId);
              if (!conf || conf.visibilidadeFluxos !== 'selecionados') return flows;
              const permitidos = conf.fluxosPermitidos || [];
              return flows.filter(f => permitidos.includes(f.id));
            })()}
            allFlows={flows}
            colaboradores={colaboradores}
            departamentos={departamentos}
            cardsResumo={cardsResumo}
            onCreateFlow={handleCreateFlow}
            onSelectFlow={handleNavigateToFlow}
            onUpdateFlow={handleUpdateFlow}
            onDeleteFlow={handleDeleteFlow}
            onCloneFlow={handleCloneFlow}
            onCreateFromTemplate={handleCreateFromTemplate}
            userPermissions={{
              canEdit: checkUserPermission('edit'),
              canAdmin: checkUserPermission('admin'),
              currentUser: session?.usuario,
              podeCriarFluxos: podeCriarFluxos()
            }}
            planoInfo={planoInfo}
            onNavigate={(section) => setCurrentSection(section)}
          />
        );

      case 'cards-conectados':
        return (
          <CardsConectados
            flows={flows}
            session={session}
            userPermissions={{
              canEdit: checkUserPermission('edit'),
              canAdmin: checkUserPermission('admin'),
              currentUser: session?.usuario
            }}
            onNavigateToFlow={handleNavigateToFlow}
          />
        );

      case 'cube':
        return (
          <Cube
            flows={flows}
            session={session}
          />
        );

      case 'departamentos':
        return (
          <Departamentos
            initialDepartamentos={departamentos}
            colaboradores={colaboradores}
            onDepartamentosChange={handleDepartamentosChange}
            session={session || undefined}
            onNavigateToProcedimentos={(departamentoId) => {
              setSelectedDepartamentoId(departamentoId);
              setCurrentSection('procedimentos');
            }}
          />
        );

      case 'colaboradores':
        return (
          <Colaboradores
          initialColaboradores={colaboradores}
          departamentos={departamentos}
          users={users}
          flows={flows}
          empresaId={session?.empresa?.id || ''}
          onColaboradoresChange={handleColaboradoresChange}
          onUserChange={handleUpdateUser}
          onUserCreate={handleCreateUser}
          onUsersReload={() => loadUsers(session)}
          userPermissions={{
            canEdit: checkUserPermission('edit'),
            canAdmin: checkUserPermission('admin'),
            currentUser: session?.usuario
          }}
        />
        );

      case 'organograma':
        const cardsVencidos = calcularCardsVencidos();
        return (
          <Organograma
            departamentos={departamentos}
            colaboradores={colaboradores}
            flows={flowsComCardsResumo}
            cardsVencidos={cardsVencidos}
            onNavigateToColaborador={(colaborador) => {
              setViewingColaborador(colaborador);
              // Só define como editando se o usuário tem permissão
              if (checkUserPermission('edit') || checkUserPermission('admin')) {
                setEditingColaborador(colaborador);
              } else {
                setEditingColaborador(null); // Apenas visualização
              }
            }}
            onNavigateToFlow={handleNavigateToFlow}
            userPermissions={{
              canEdit: checkUserPermission('edit'),
              canAdmin: checkUserPermission('admin'),
              currentUser: session?.usuario
            }}
          />
        );

      case 'procedimentos':
        return (
          <ProcedimentosSimples
            session={session || undefined}
            departamentoIdFiltro={selectedDepartamentoId}
          />
        );

      case 'usuarios':
        return (
          <div className="p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Gerenciamento de Usuários</h2>
            <p className="text-gray-600">
              O gerenciamento de usuários agora é feito através da seção "Colaboradores".
              Acesse a seção Colaboradores para convidar novos usuários e gerenciar permissões.
            </p>
          </div>
        );

      case 'automacoes':
        return (
          <Automacoes
            flows={flows}
            automacoes={automacoes}
            onSave={handleAutomacoesChange}
            session={session ? {
              usuario: {
                role: session.usuario.role,
                empresaId: session.empresa?.id || session.usuario.empresaId
              }
            } : undefined}
            canView={checkMenuAccess('automacoes').canView}
            canEdit={checkMenuAccess('automacoes').canEdit}
            allowedFlowIds={(() => {
              if (checkUserPermission('admin')) return undefined;
              const u: any = session?.usuario;
              const perm = (u?.permissoes?.menus || []).find((p: any) => p.modulo === 'automacoes');
              return perm?.fluxosIds?.length > 0 ? perm.fluxosIds : undefined;
            })()}
          />
        );

      case 'emails':
        if (!checkUserPermission('admin') && !checkMenuAccess('emails').canView)
          return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito a administradores.</div>;
        return <Emails session={session} />;
      case 'whatsapp':
        if (!checkUserPermission('admin') && !checkMenuAccess('whatsapp').canView)
          return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito a administradores.</div>;
        return <Whatsapp session={session} />;

      case 'integracoes':
        if (!checkUserPermission('admin') && !checkMenuAccess('integracoes').canView)
          return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito a administradores.</div>;
        return <IntegracoesPage session={session} />;

      case 'crm':
      case 'crm-pipeline':
        if (!checkCRMAccess()) return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito ao time Comercial.</div>;
        return <CRMPipeline session={session} />;
      case 'crm-minha-carteira':
        return <CRMMinhaCarteira session={session} />;
      case 'crm-carteira-gix':
        return <CRMCarteiraGix session={session} />;
      case 'crm-leads':
        if (!checkCRMAccess()) return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito ao time Comercial.</div>;
        return <CRMLeads session={session} />;
      case 'crm-contatos':
        if (!checkCRMAccess()) return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito ao time Comercial.</div>;
        return <CRMContatos session={session} />;
      case 'crm-empresas':
        if (!checkCRMAccess()) return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito ao time Comercial.</div>;
        return <CRMEmpresasConta session={session} />;
      case 'crm-comunicacao':
        if (!checkCRMAccess()) return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito ao time Comercial.</div>;
        return <CRMComunicacao session={session} initialInboxNumero={crmInboxNumero} onInboxNumeroConsumed={() => setCrmInboxNumero(undefined)} />;
      case 'crm-configuracoes':
        if (!checkUserPermission('admin')) return <div className="p-8 text-center text-gray-500">🔒 Acesso restrito a administradores.</div>;
        return <CRMConfiguracoes session={session} />;

      case 'planos':
        return (
          <Planos
            planoInfo={planoInfo}
            empresaNome={session?.empresa?.nome}
            session={session}
            onVoltar={() => setCurrentSection('home')}
            onContratarPlano={(plano) => {
              console.log('🛒 Plano selecionado:', plano);
              setCheckoutPlano(plano);
            }}
          />
        );

      default:
        return (
          <div className="flex-1 p-8 flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {menuItems.find(item => item.id === currentSection)?.name || 
                 menuItems.find(item => item.subItems?.some(sub => sub.id === currentSection))
                  ?.subItems?.find(sub => sub.id === currentSection)?.name}
              </h2>
              <p className="text-gray-500">
                {menuItems.find(item => item.subItems?.some(sub => sub.id === currentSection))
                  ?.subItems?.find(sub => sub.id === currentSection)?.description || 
                 'Esta seção está em desenvolvimento.'}
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {/* 🔗 Loading enquanto processa link direto para card */}
      {loadingShortCode && (
        <div className="fixed inset-0 z-[9999]">
          <EscaloneLoading 
            title="Abrindo "
            subtitle="seu card"
            description="Estamos localizando e carregando o card solicitado. Em instantes você terá acesso completo às informações."
            steps={['Localizando card', 'Carregando dados', 'Preparando visualização', 'Abrindo card']}
          />
        </div>
      )}
      {/* 🔗 Loading enquanto processa link direto para fluxo */}
      {loadingFlowUrl && !loadingShortCode && (
        <div className="fixed inset-0 z-[9999]">
          <EscaloneLoading 
            title="Carregando "
            subtitle="seu fluxo"
            description="Estamos localizando e carregando o fluxo solicitado. Em instantes você terá acesso ao Kanban."
            steps={['Restaurando sessão', 'Carregando fluxos', 'Localizando fluxo', 'Abrindo Kanban']}
          />
        </div>
      )}
      {/* 🔗 Loading enquanto processa link direto para procedimento */}
      {loadingProcedimentoUrl && !loadingShortCode && !loadingFlowUrl && (
        <div className="fixed inset-0 z-[9999]">
          <EscaloneLoading 
            title="Abrindo "
            subtitle="seu procedimento"
            description="Estamos localizando e carregando o procedimento solicitado. Em instantes você terá acesso aos detalhes."
            steps={['Restaurando sessão', 'Localizando departamento', 'Carregando procedimento', 'Abrindo visualização']}
          />
        </div>
      )}
      {fatalError && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-bold mb-2">Falha ao carregar dados</h1>
            <p className="text-gray-600 mb-4">{fatalError}</p>
            <ul className="text-left text-sm text-gray-700 list-disc list-inside mb-6">
              <li>Verifique se o Backend Flask está rodando em http://localhost:5001</li>
              <li>Verifique se o MongoDB está acessível</li>
              <li>Recarregue a página após estabilizar os serviços</li>
            </ul>
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-black text-white rounded">Tentar novamente</button>
          </div>
        </div>
      )}
  {/* Rota pública do Portal - funciona com ou sem login */}
      {window.location.pathname.startsWith('/portal/') ? (
        <Routes>
          <Route path="/portal/:token" element={<PortalPublico />} />
        </Routes>
      ) : window.location.pathname.startsWith('/disc/') ? (
        /* Link de DISC que o RH manda — funciona com ou sem login */
        <Routes>
          <Route path="/disc/:token" element={<DiscPublico />} />
        </Routes>
      ) : !session ? (
        <Routes>
          {/* Rota pública da Landing */}
          <Route path="/" element={<Landing />} />
          <Route path="/auth/redirect" element={<AuthRedirect onLogout={handleLogout} />} />
          <Route path="/termos" element={<Terms />} />
          <Route path="/cadastro" element={
            verificacaoEmail ? (
              <Verificacao
                email={verificacaoEmail.email}
                onVerificar={handleVerificarEmail}
                onCancel={() => setVerificacaoEmail(null)}
              />
            ) : (
              <Cadastro 
                onSignUp={handleSignUp}
                onPaymentSuccess={handlePaymentSuccess}
                onTrialNeedsVerification={(email, codigo, senha) => setVerificacaoEmail({ email, codigo, senha })}
                onCancel={() => navigate('/')}
              />
            )
          } />
          <Route path="/login" element={
            <Login 
              onLogin={handleLogin} 
              onSelectEmpresa={handleSelectEmpresa}
              empresas={empresas}
              onSignUpClick={() => navigate('/cadastro')}
              tempUser={tempUser}
              step={step}
              loadingEmpresas={loading.isLoading && loading.type === 'empresas'}
            />
          } />
          <Route path="/reset-password" element={
            <ResetPassword 
              onReset={handleResetPassword}
              onCancel={() => navigate('/login')}
            />
          } />
          {/* Rota para abrir card pelo shortCode (6-7 dígitos) */}
          <Route path="/c/:shortCode" element={<CardRedirect />} />
          {/* Rota para abrir tarefa pelo ID */}
          <Route path="/t/:taskId" element={<TaskRedirect />} />
          {/* Rota pública para formulários de Portais */}
          <Route path="/portal/:token" element={<PortalPublico />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : !selectedProduct ? (
        /* 🏦 Tela de seleção de produto (Escalone ou Finance) */
        <ProductSelector
          userName={session.usuario?.nome || ''}
          onSelectProduct={handleSelectProduct}
        />
      ) : selectedProduct === 'finance' ? (
        /* 💰 Escalone Finance */
        <FinanceApp
          session={session}
          onVoltar={handleVoltarParaSelecao}
        />
      ) : selectedProduct === 'atlas' ? (
        /* 👥 Atlas HCM */
        <AtlasApp
          session={session as any}
          onVoltar={handleVoltarParaSelecao}
        />
      ) : (
        <SocketProvider session={session}>
          <div className="h-screen flex bg-gradient-to-br from-white via-white to-purple-50 relative">
            {/* � Easter egg: arcade jogável ao clicar no logo "E" */}
            {activeIntro === 1 && <RamboArcade onFinish={() => setActiveIntro(0)} />}
            {/* Overlay mobile */}
            {menuOpen && (
            <div
              className="fixed inset-0 z-30 bg-black/40 md:hidden"
              onClick={() => setMenuOpen(false)}
            />
          )}

          {/* Menu Lateral - responsivo */}
          <div
            className={`fixed inset-y-0 left-0 z-40 w-52 bg-white/80 backdrop-blur-md border-r border-gray-200/50 flex flex-col shadow-sm transform transition-transform duration-300
            ${menuOpen ? 'translate-x-0' : '-translate-x-full'}
            md:static md:inset-auto md:transform-none ${menuOpen ? 'md:w-52' : 'md:w-14'}`}
          >
            <div className="p-2 flex items-center justify-between border-b border-gray-200/50">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={playNextIntro}
                  title="� easter egg"
                  className="w-7 h-7 rounded-lg bg-gradient-to-r from-gray-900 to-black text-white flex items-center justify-center font-bold text-xs shadow-md flex-shrink-0 hover:scale-110 active:scale-95 transition-transform cursor-pointer"
                >
                  E
                </button>
                {menuOpen && (
                  <h1 className="font-extrabold text-base text-gray-900 tracking-tight truncate">
                    Escalone
                  </h1>
                )}
              </div>
              {menuOpen && (
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="p-1 hover:bg-gray-100 rounded-md text-gray-500 transition-colors flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Botão expandir quando fechado */}
            {!menuOpen && (
              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 mx-auto mt-1 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
              >
                <LucideMenu className="h-4 w-4" />
              </button>
            )}
           
            <nav className="flex-1 p-3 space-y-1">
              {menuItems.filter(item => {
                if (item.id === 'crm') return checkCRMAccess() || checkMenuAccess('crm').canView;
                return true;
              }).map((item) => {
                // Filtrar sub-itens: os de "configuracoes" exigem admin ou permissão explícita
                const subItemsFiltrados = item.subItems?.filter(subItem => {
                  const ADMIN_ONLY_SUBITEMS = ['automacoes', 'emails', 'whatsapp', 'integracoes', 'planos'];
                  if (ADMIN_ONLY_SUBITEMS.includes(subItem.id)) {
                    return checkUserPermission('admin') || checkMenuAccess(subItem.id).canView;
                  }
                  return true;
                });
                // Ocultar item pai de "configuracoes" se todos os sub-itens foram filtrados
                if (item.subItems && subItemsFiltrados && subItemsFiltrados.length === 0) return null;
                return (
                <div key={item.id} className="relative" data-tour={`menu-${item.id}`}>
                  <button
                    onClick={() => {
                      if (item.subItems) {
                        toggleSubMenu(item.id);
                        // Para Fluxos: navegar também ao clicar
                        if (item.id === 'flows') {
                          navigateToSection(item.id);
                        }
                      } else {
                        navigateToSection(item.id);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg transition-all duration-200 group ${
                      currentSection === item.id
                        ? 'bg-gradient-to-r from-gray-900 to-black text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100/80 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <item.icon className={`h-4 w-4 ${currentSection === item.id ? 'text-white' : 'text-gray-600 group-hover:text-gray-900'}`} />
                        {/* 🔔 Badge de mensagens não lidas do FlowChat (some ao abrir a conversa).
                            Fica no ícone para continuar visível com o menu recolhido. */}
                        {item.id === 'chat' && chatNaoLidas > 0 && (
                          <span className="absolute -top-1.5 -right-2 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-1 flex items-center justify-center shadow">
                            {chatNaoLidas > 99 ? '99+' : chatNaoLidas}
                          </span>
                        )}
                      </div>
                      {menuOpen && <span className="font-medium text-sm">{item.name}</span>}
                    </div>
                    {menuOpen && item.subItems && (
                      <LucideChevronDown 
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${
                          expandedMenus.includes(item.id) ? 'transform rotate-180' : ''
                        } ${currentSection === item.id ? 'text-white' : 'text-gray-500'}`}
                      />
                    )}
                  </button>

                  {menuOpen && subItemsFiltrados && expandedMenus.includes(item.id) && (
                    <div className="ml-5 mt-1 space-y-0.5">
                      {subItemsFiltrados.map(subItem => (
                        <button
                          key={subItem.id}
                          data-tour={`menu-${subItem.id}`}
                          onClick={() => navigateToSection(subItem.id)}
                          className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all duration-200 group ${
                            currentSection === subItem.id
                              ? 'bg-indigo-50 text-indigo-700 font-semibold border border-indigo-200'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <subItem.icon className={`h-3.5 w-3.5 ${
                            currentSection === subItem.id ? 'text-indigo-600' : 'text-gray-500 group-hover:text-gray-700'
                          }`} />
                          <span className="text-xs">{subItem.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </nav>

            {/* Trocar Módulo */}
            <div className="px-3 pt-2">
              <button
                onClick={handleVoltarParaSelecao}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-gray-600 hover:bg-gray-100/80 hover:text-gray-900 transition-all duration-200 group ${menuOpen ? '' : 'justify-center'}`}
              >
                <ArrowLeftRight className="h-4 w-4 text-gray-500 group-hover:text-gray-700" />
                {menuOpen && <span className="font-medium text-xs">Trocar Módulo</span>}
              </button>
            </div>

            {/* Menu do Usuário */}
            <div className="p-3 border-t border-gray-200/50">
              <div className="relative">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100/80 transition-all duration-200 group ${menuOpen ? '' : 'justify-center'}`}
                >
                  <div className="h-6 w-6 rounded-md bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs shadow-sm">
                    {session.usuario.nome[0].toUpperCase()}
                  </div>
                  {menuOpen && (
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs text-gray-900 truncate">{session.usuario.nome}</div>
                      <div className="text-[10px] text-gray-500 truncate">{session.usuario.email}</div>
                      {session.empresa && (
                        <div className="text-[10px] text-indigo-600 font-medium truncate">{session.empresa.nome}</div>
                      )}
                    </div>
                  )}
                </button>

                {showUserMenu && (
                  <div className={`absolute bottom-full mb-2 bg-white rounded-lg shadow-xl border border-gray-200/50 py-1.5 backdrop-blur-md ${menuOpen ? 'left-0 w-full' : 'left-1/2 -translate-x-1/2 w-max'}`}>
                    <button
                      onClick={handleLogout}
                      className={`px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors rounded-md ${menuOpen ? 'w-full text-left mx-1.5' : 'justify-center'}`}
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      {menuOpen && <span className="font-medium">Sair</span>}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Conteúdo Principal */}
          <div className="flex-1 flex flex-col min-h-0 overflow-x-hidden overflow-y-auto">
            {/* Top bar mobile */}
            <div className="md:hidden sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
              <div className="flex items-center justify-between px-3 py-2">
                <button
                  onClick={() => setMenuOpen(true)}
                  className="p-1.5 rounded-md text-gray-700 hover:bg-gray-100"
                  aria-label="Abrir menu"
                >
                  <LucideMenu className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={playNextIntro}
                    className="w-6 h-6 rounded-lg bg-gradient-to-r from-gray-900 to-black text-white flex items-center justify-center font-bold text-xs active:scale-95 transition-transform"
                    title="🎬"
                  >
                    E
                  </button>
                  <span className="font-semibold text-sm text-gray-900">Escalone</span>
                </div>
                <div className="w-5" />
              </div>
            </div>
            {/* Banner de trial ativo */}
            {planoInfo?.trial_ativo && !trialBannerDismissed && (
              <TrialBanner
                diasRestantes={planoInfo.dias_restantes ?? 0}
                onUpgrade={() => setCurrentSection('planos')}
                onDismiss={() => setTrialBannerDismissed(true)}
              />
            )}
            {renderMainContent()}
          </div>

          {/* Modal de Visualização/Edição de Colaborador */}
          {viewingColaborador && (
            <ColaboradorModal
              colaborador={viewingColaborador}
              departamentos={departamentos}
              colaboradores={colaboradores}
              existingUsers={users}
              onSave={handleEditColaborador}
              onSaveAuth={handleUpdateUser}
              onClose={() => {
                setViewingColaborador(null);
                setEditingColaborador(null);
              }}
              empresaId={session?.empresa?.id || ''}
              userPermissions={{
                canEdit: checkUserPermission('edit') && editingColaborador !== null,
                canAdmin: checkUserPermission('admin'),
                currentUser: session?.usuario
              }}
            />
          )}
          
          {/* FlowChat - sempre renderizado, mas minimizado quando não em uso */}
          <Chat
            key={chatOpenKey} // Força re-mount quando muda
            session={session}
            flows={flows}
            departamentos={departamentos}
            colaboradores={colaboradores}
            initialOpen={chatOpenKey > 0}
            initialChannelId={chatCanalInicial}
            initialChannelNome={chatCanalNome}
            onClose={() => {}}
          />

          {/* 🔔 Notificação de mensagem em qualquer tela (fluxo, card, CRM...).
              Fica montado sempre — o FlowChat só existe quando aberto, então
              sem isso ninguém era avisado fora da tela de chat. */}
          <ChatNotifier session={session} />
        </div>
        </SocketProvider>
      )}
      
      {/* Tela de upgrade quando trial expirou */}
      {planoInfo?.trial_expirado && !showPlanosScreen && (
        <UpgradeScreen
          empresaNome={session?.empresa?.nome}
          planoInfo={planoInfo}
          onUpgrade={() => setShowPlanosScreen(true)}
          onLogout={handleLogout}
          onContratarPlano={(plano) => {
            console.log('🛒 Plano selecionado (upgrade):', plano);
            setCheckoutPlano(plano);
          }}
        />
      )}
      {showPlanosScreen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-white">
          <Planos
            planoInfo={planoInfo}
            empresaNome={session?.empresa?.nome}
            modo="pagina"
            onVoltar={() => setShowPlanosScreen(false)}
            onContratarPlano={(plano) => {
              console.log('🛒 Plano selecionado (screen):', plano);
              setCheckoutPlano(plano);
            }}
          />
        </div>
      )}

      {/* Indicador de progresso de carregamento */}
      <DataLoadingIndicator />

      {/* Checkout modal de pagamento */}
      {checkoutPlano && (
        <CheckoutModal
          plano={checkoutPlano}
          userEmail={session?.usuario?.email || ''}
          userName={session?.usuario?.nome || ''}
          onClose={() => setCheckoutPlano(null)}
          onSuccess={() => {
            setCheckoutPlano(null);
            // Recarregar a página para atualizar planoInfo
            window.location.reload();
          }}
        />
      )}

      {/* Tour de onboarding para primeiro acesso */}
      {showOnboarding && !planoInfo?.trial_expirado && (
        <OnboardingTour
          empresaNome={session?.empresa?.nome}
          usuarioNome={session?.usuario?.nome}
          onClose={() => {
            setShowOnboarding(false);
            const tourKey = `onboarding_dismissed_${session?.usuario?.id || session?.usuario?.email}`;
            localStorage.setItem(tourKey, 'true');
          }}
          onNavigate={(section) => {
            setShowOnboarding(false);
            const tourKey = `onboarding_dismissed_${session?.usuario?.id || session?.usuario?.email}`;
            localStorage.setItem(tourKey, 'true');
            setCurrentSection(section);
          }}
          onExpandMenu={(menuId) => {
            if (!menuOpen) setMenuOpen(true);
            if (!expandedMenus.includes(menuId)) {
              setExpandedMenus(prev => [...prev, menuId]);
            }
          }}
        />
      )}
    </>
  );
};

const AppWithProvider = () => {
  return (
    <LoadingProvider>
      <ToastProvider>
        <DataLoadingProvider>
          <App />
          <AvisoNovaVersao />
        </DataLoadingProvider>
      </ToastProvider>
    </LoadingProvider>
  );
};

export default AppWithProvider;
